import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { PipeClassViewModel, PressureTemperatureTableViewModel, PressureTemperatureTableNoteViewModel, EsraPipingAPIClient, ServiceViewModel, SearchRequest, PipeClassPropertyViewModel, RatingClassViewModel, DesignCodeViewModel, TemperatureRangeViewModel, PipeClassNoteViewModel, CommonNoteViewModel, RevisionStatus } from '../../shared/models/autogenerated-piping';
import { CommentViewModel } from '../../shared/models/comment.model';
import { AddPressureTempTableComponent, PressureTempTableModalData } from 'src/app/piping-valves/revisions/add-pressure-temp-table/add-pressure-temp-table.component';
import { MatDialog } from '@angular/material/dialog';
import { ServiceSelectionModalComponent, ServiceOption } from './service-selection-modal/service-selection-modal.component';
import { NotesSelectionModalComponent, NoteOption } from './notes-selection-modal/notes-selection-modal.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { PropertySelectionModalComponent, PropertySelectionData } from './property-selection-modal/property-selection-modal.component';
import { PTTSyncService } from '../services/ptt-sync.service';
import type { PressureTemperatureTableValidationResult } from '../services/ptt-sync.service';
import { UserService } from '../../core/services/user.service';
import { Constants } from '../../shared/constants/constants';

// General Note interface for free text notes
interface GeneralNote {
  id?: string;
  text: string;
  createdDate?: Date;
  modifiedDate?: Date;
}

interface AdditionalNoteEditState {
  tableIndex: number;
  noteIndex: number;
  isEditing: boolean;
  editText: string;
}



interface UnifiedAdditionalNote {
  text: string;
  isEditable: boolean;
  source: 'existing' | 'relation' | 'new';
}

interface UnifiedCommonNote {
  id: string;
  code: string;
  note: string;
  isEditable: boolean;
  source: 'existing' | 'relation' | 'new';
}

interface UnifiedTestNote {
  note: string;
  type?: string;
  isEditable: boolean;
  source: 'existing' | 'relation' | 'new';
}

// Helper function to process relation notes from the unified collection
function processRelationNotes(table: PressureTemperatureTableViewModel): {
  relationTestNotes: any[],
  relationAdditionalNotes: any[]
} {

  const allRelationNotes = (table as any)?.pipeClassPressureTemperatureTableRelationNotes ||
                           (table as any)?.relationNotes ||
                           (table as any)?.pressureTemperatureTableRelationNotes;

  if (allRelationNotes && Array.isArray(allRelationNotes)) {

    // Separate notes by category
    const testNotes = allRelationNotes.filter(note =>
      note.type === 'Test' || note.type === 'test'
    );

    // Additional notes include: Additional, NpsRangeInfo, PtrNote, ServiceNote, etc.
    const additionalNotes = allRelationNotes.filter(note =>
      note.type !== 'Test' && note.type !== 'test'
    );

    return {
      relationTestNotes: testNotes,
      relationAdditionalNotes: additionalNotes
    };
  }

  if (table.relationTestNotes || table.relationAdditionalNotes) {
    const filteredTestNotes = (table.relationTestNotes || []).filter(note =>
      note.type === 'Test' || note.type === 'test'
    );

    // Keep all additional notes regardless of type
    const filteredAdditionalNotes = table.relationAdditionalNotes || [];

    return {
      relationTestNotes: filteredTestNotes,
      relationAdditionalNotes: filteredAdditionalNotes
    };
  }

  return {
    relationTestNotes: [],
    relationAdditionalNotes: []
  };
}

@Component({
    selector: 'app-piping-summary',
    styleUrls: ['piping-summary.component.scss'],
    templateUrl: 'piping-summary.component.html',
    standalone: false
})
export class PipingSummaryComponent implements OnInit, OnDestroy {
  private _pipeClassViewModel: PipeClassViewModel;

  @Input()
  set pipeClassViewModel(value: PipeClassViewModel) {

    const previousTableCount = this._pipeClassViewModel?.pressureTemperatureTables?.length || 0;
    const newTableCount = value?.pressureTemperatureTables?.length || 0;

    this._pipeClassViewModel = value;

    if (previousTableCount === 0 || newTableCount < previousTableCount) {
      this.pttNotesInitialized = false;
    }

    // If we have PTT tables and the component has been initialized, validate them
    if (value?.pressureTemperatureTables?.length > 0) {

      // Clear existing sync states since we have a new pipeClassViewModel
      this.pttSyncStates.clear();
      this.pttSyncLoading.clear();
      this._loggedSyncStates?.clear();

      // Validate the new tables
      setTimeout(() => {
        this.validateAllPTTTables();
      }, 100); // Small delay to ensure everything is set up
    }

    // Reload general notes when pipeClassViewModel changes
    if (value && value.customNotes) {
      this.loadGeneralNotes();
    }
  }

  get pipeClassViewModel(): PipeClassViewModel {
    return this._pipeClassViewModel;
  }

  @Input() canEdit$: Observable<boolean>; // Received from parent component
  @Input() isCompareMode: boolean = false; // Compare mode flag from parent
  @Input() isInRevisionWorkflow: boolean = false; // True for any user who arrived from the revisions dashboard (authors + reviewers)
  @Input() previousPipeClassViewModel: PipeClassViewModel | null = null; // Previous version for comparison
  @Input() comments: CommentViewModel[] = []; // Comments for this tab
  @Input() commentsLoading$: Observable<boolean>; // True while comments are being fetched
  @Output() notesAddedEvent = new EventEmitter<{notes: any[], origin: string}>();
  @Output() notesRemovedEvent = new EventEmitter<{notes: any[], origin: string}>();
  @Output() generalNotesChangedEvent = new EventEmitter<{notes: GeneralNote[], pipeClassId?: string}>();
  @Output() commentsChanged = new EventEmitter<CommentViewModel[]>();
  @Output() saveCommentsRequested = new EventEmitter<void>(); // New event for auto-save
  @Output() propertyChanged = new EventEmitter<{field: string, value: any, section: string}>(); // New event for property changes

  openState = true;
  serviceDisplayName = '';
  showRelationalNotes = false;
  isEditingTemperatureLimit = false;
  isEditingRatingClass = false;
  isEditingDesignCode = false;
  isAddingNewService = false;
  newServiceForm: FormGroup;
  // General Notes CRUD properties
  generalNotesList: GeneralNote[] = []; // List of free text general notes

  // Force UI refresh tracker
  private uiRefreshVersion = 0;

  // PTT Notes CRUD properties - each PTT table has its own notes array
  pttNotesChanges: NoteOption[][] = []; // Array of arrays - each PTT table has its own notes list

  // Hydrotest notes for each PTT table - array of strings where each index corresponds to a PTT table
  pttHydrotestNotes: string[] = []; // Array of strings - each PTT table has its own hydrotest note

  pttAdditionalNotes: UnifiedAdditionalNote[][] = []; // Array of arrays - each PTT table has its own list of additional notes

  pttCommonNotes: UnifiedCommonNote[][] = []; // Array of arrays - each PTT table has its own list of unified common notes

  pttTestNotes: UnifiedTestNote[][] = []; // Array of arrays - each PTT table has its own list of unified test notes

  // Flag to track if PTT notes have been initialized to prevent overwriting user edits
  private pttNotesInitialized = false;

  additionalNoteEditState: AdditionalNoteEditState | null = null;
  newAdditionalNoteText: { [tableIndex: number]: string } = {}; // Store new note text per table
  temperatureLimitForm: FormGroup;
  ratingClassForm: FormGroup;
  designCodeForm: FormGroup;

  // Comments properties
  pipeClassComments: CommentViewModel[] = [];

  // PTT Synchronization properties
  pttSyncStates: Map<number, PressureTemperatureTableValidationResult> = new Map();
  pttSyncLoading: Map<number, boolean> = new Map();
  viewRefreshCounter = 0;
  pttTableRenderKey = 0;  // Forces template re-render when changed
  pttComponentVisible = true; // Toggle to force component destruction/recreation
  private _loggedSyncStates?: Set<string>;
  private _lastLoadingState?: { [key: number]: boolean };
  private allAvailableServices: ServiceViewModel[] = [];

  /** True when the current user has the piping dictionary writer role and can open the PTT editor. */
  canEditPTTDictionary: boolean = false;

  /** BroadcastChannel used to receive PTT-saved notifications from the dictionary editor tab. */
  private pttUpdateChannel: BroadcastChannel | null = null;

  constructor(
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private esraApiClient: EsraPipingAPIClient,
    private pttSyncService: PTTSyncService,
    private userService: UserService
  ) {
    this.temperatureLimitForm = this.formBuilder.group({
      minimumTemperature: ['', [Validators.required, Validators.pattern(/^-?\d+(\.\d+)?$/)]],
      maximumTemperature: ['', [Validators.required, Validators.pattern(/^-?\d+(\.\d+)?$/)]]
    });

    this.ratingClassForm = this.formBuilder.group({
      ratingClass: ['', [Validators.required]]
    });

    this.designCodeForm = this.formBuilder.group({
      designCode: ['', [Validators.required]]
    });

    this.newServiceForm = this.formBuilder.group({
      serviceName: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.loadAvailableServices();
    this.loadGeneralNotes();
    this.initializePTTNotes();
    this.validateAllPTTTables();
    this.canEditPTTDictionary =
      this.userService.isRoleExists(Constants.pipingDictionaryWriter) ||
      this.userService.isRoleExists(Constants.admin) ||
      this.userService.isRoleExists(Constants.scanner);
    this.listenForPTTDictionaryUpdates();
  }

  ngOnDestroy(): void {
    if (this.pttUpdateChannel) {
      this.pttUpdateChannel.close();
      this.pttUpdateChannel = null;
    }
  }

  /**
   * Opens the PTT dictionary editor in a new tab for the given pressure-temperature table.
   * Uses localStorage to pass the table data (the same mechanism the admin dictionary list uses).
   * Only available to users with the pipingDictionaryWriter role.
   */
  openPTTEditor(table: PressureTemperatureTableViewModel): void {
    localStorage.setItem('pressureTemperatureTable', JSON.stringify(table));
    window.open('path/pressureTemperatureAddEdit', '_blank');
  }

  /**
   * Subscribes to cross-tab PTT-saved events via BroadcastChannel.
   * When the dictionary editor in another tab saves a PTT that is referenced by this revision,
   * the cached validation state for that table is cleared and re-validated so the sync
   * button becomes enabled without requiring a page refresh.
   */
  private listenForPTTDictionaryUpdates(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    this.pttUpdateChannel = new BroadcastChannel('ptt-dictionary-updated');
    this.pttUpdateChannel.onmessage = (event: MessageEvent) => {
      const tableId: string | undefined = (event.data as { tableId?: string })?.tableId;
      if (!tableId || !this.pipeClassViewModel?.pressureTemperatureTables) {
        return;
      }
      const tableIndex: number = this.pipeClassViewModel.pressureTemperatureTables
        .findIndex(t => t.id === tableId);
      if (tableIndex >= 0) {
        // Clear cached state so validatePTTTable fetches fresh data
        this.pttSyncStates.delete(tableIndex);
        this.validatePTTTable(tableIndex);
      }
    };
  }

  /**
   * Marks that the summary table has been changed and sets the last updated revision
   */
  private markSummaryTableChanged(): void {
    if (this.pipeClassViewModel) {
      this.pipeClassViewModel.summaryTableChanged = true;
    }
  }

  getLastSegment(revisionName: string | undefined): string {
    if (!revisionName || revisionName.trim() === '') return '';
    const segments = revisionName.split('-');
    return segments[segments.length - 1] || '';
  }

  getSummaryRevision(): string {
    if (!this.pipeClassViewModel) return '';

    const fromRevision: string = this.pipeClassViewModel.fromRevision?.name || '';
    const storedRevision: string = this.pipeClassViewModel.summaryTableLastUpdatedRevision || '';
    // Edit mode: revision is not yet published (Draft, Under Review, or Ready For Publish)
    const isRevisionInProgress: boolean = this.pipeClassViewModel.fromRevision?.status !== undefined
      && this.pipeClassViewModel.fromRevision?.status !== null
      && this.pipeClassViewModel.fromRevision.status !== RevisionStatus.PUBLISHED;

    if (isRevisionInProgress) {
      // Revision is in progress: only show if the summary table was changed in this revision
      if (this.pipeClassViewModel.summaryTableChanged) {
        return this.getLastSegment(fromRevision);
      }
      if (storedRevision && storedRevision === fromRevision) {
        return this.getLastSegment(storedRevision);
      }
      return '';
    }

    // Published/viewing mode: show lastUpdatedRevision if available, fallback to fromRevision
    if (storedRevision) {
      return this.getLastSegment(storedRevision);
    }
    if (fromRevision) {
      return this.getLastSegment(fromRevision);
    }
    return '';
  }

  /**
   * Marks that a specific PTT has been changed and sets the last updated revision
   */
  private markPTTChanged(tableIndex: number): void {
    if (this.pipeClassViewModel?.pressureTemperatureTables?.[tableIndex]) {
      const table = this.pipeClassViewModel.pressureTemperatureTables[tableIndex];
      table.changed = true;
    }
  }

  private loadAvailableServices(): void {
    this.esraApiClient.getAllCurrentServices()
      .pipe(
        first(),
        tap(services => {
          this.allAvailableServices = services;
        })
      )
      .subscribe({
        next: (services) => {
        },
        error: (error) => {
          // Error loading services
        }
      });
  }

  private formatServiceDate(date: Date): string {
    if (!date) return 'No date available';
    try {
      return String(date.getMonth() + 1).padStart(2, '0') + '/' +
             String(date.getDate()).padStart(2, '0') + '/' +
             date.getFullYear();
    } catch (error) {
      return 'Invalid date';
    }
  }

  // PTT Synchronization Methods
  private validateAllPTTTables(): void {
    if (!this.pipeClassViewModel?.pressureTemperatureTables) {
      return;
    }

    if (this.pipeClassViewModel.pressureTemperatureTables.length === 0) {
      return;
    }

    this.pipeClassViewModel.pressureTemperatureTables.forEach((table, index) => {
      if (!this.pttSyncStates.has(index)) {
        this.validatePTTTable(index);
      }
    });
  }

  private validatePTTTable(tableIndex: number): void {
    const table = this.pipeClassViewModel?.pressureTemperatureTables?.[tableIndex];
    if (!table || !table.id) {
      return;
    }

    const existingState = this.pttSyncStates.get(tableIndex);
    if (existingState) {
      return;
    }

    this.pttSyncLoading.set(tableIndex, true);
    this.pttSyncService.validateAndGetLatestPTT(table.id.toString())
      .pipe(first())
      .subscribe({
        next: (result) => {
          this.pttSyncStates.set(tableIndex, result);
          this.pttSyncLoading.set(tableIndex, false);
          this.cdr.detectChanges();
        },
        error: () => {
          this.pttSyncLoading.set(tableIndex, false);
          const fallbackState = {
            isCurrent: true,
            message: 'Validation failed'
          };
          this.pttSyncStates.set(tableIndex, fallbackState);
          this.cdr.detectChanges();
        }
      });
  }



  /**
   * PTT Sync: Automatically replace outdated table with latest version
   * PRESERVES RELATION NOTES from old table
   */
  syncPTTTable(tableIndex: number): void {

    const syncState = this.pttSyncStates.get(tableIndex);

    if (!syncState || syncState.isCurrent || !syncState.latestVersion) {
      return;
    }

    // Convert to proper instance using fromJS
    const latestVersionInstance = PressureTemperatureTableViewModel.fromJS(syncState.latestVersion);



    this.removePTTTable(tableIndex);

    // Small delay to ensure delete operation completes
    setTimeout(() => {
      const modalResult: PressureTemperatureTableViewModel[] = [latestVersionInstance];
      this.handlePTTModalResult(modalResult).then(() => {
        const tables = this.pipeClassViewModel.pressureTemperatureTables;
        const newTableIndex = tables.findIndex(t => t.id === latestVersionInstance.id);
        if (newTableIndex >= 0) {
          this.pttSyncStates.set(newTableIndex, {
            isCurrent: true,
            latestVersion: latestVersionInstance,
            message: 'Updated to latest version with preserved relations'
          });

          const allNotesToAdd: any[] = [];
          const syncedTable = tables[newTableIndex];

          if (syncedTable.commonNotes && syncedTable.commonNotes.length > 0) {
            allNotesToAdd.push(...syncedTable.commonNotes);
          }

          if (syncedTable.relationCommonNotes && syncedTable.relationCommonNotes.length > 0) {
            allNotesToAdd.push(...syncedTable.relationCommonNotes);
          }

          if (allNotesToAdd.length > 0) {
            this.notesAddedEvent.emit({
              notes: allNotesToAdd,
              origin: `ptt-${syncedTable.id}-sync`
            });
          }
        }
        this.pttComponentVisible = false;
        this.cdr.detectChanges();

        setTimeout(() => {
          this.pttComponentVisible = true;
          this.cdr.detectChanges();
        }, 50);

      })
    }, 100);
  }

  trackPTTTable(index: number, table: any): string {
    return `${table?.id || index}_${this.pttTableRenderKey}`;
  }

  getPTTTableKey(table: any): string {
    return `${table?.id}_${this.pttTableRenderKey}`;
  }


  forceViewRefresh(): void {
    this.viewRefreshCounter++;
    this.pttTableRenderKey++;

    // Force array reference change by creating new array
    if (this.pipeClassViewModel?.pressureTemperatureTables) {
      const currentTables = this.pipeClassViewModel.pressureTemperatureTables;
      this.pipeClassViewModel.pressureTemperatureTables = [...currentTables];
    }

    // Force complete change detection
    this.cdr.markForCheck();
    this.cdr.detectChanges();

    // Secondary refresh cycle
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 10);
  }

  getPTTSyncTooltip(tableIndex: number): string {
    const syncState = this.pttSyncStates.get(tableIndex);
    const isLoading = this.pttSyncLoading.get(tableIndex);

    if (isLoading) {
      return 'Checking for updates...';
    } else if (!syncState) {
      return 'Unable to check for updates';
    } else if (syncState.isCurrent) {
      return 'This table is up to date';
    } else {
      return `New version available - Click to update this table${syncState.message ? ': ' + syncState.message : ''}`;
    }
  }

  /**
   * Returns `true` if any PTT table in the current pipe class is confirmed out of sync.
   * Used by the parent component to gate the "Confirm Info to Review" action.
   */
  hasUnsyncedPTTTables(): boolean {
    if (!this.pipeClassViewModel?.pressureTemperatureTables?.length) {
      return false;
    }
    return this.pipeClassViewModel.pressureTemperatureTables.some((_, index) => {
      const syncState: PressureTemperatureTableValidationResult | undefined = this.pttSyncStates.get(index);
      return syncState !== undefined && !syncState.isCurrent;
    });
  }

  isPTTSyncEnabled(tableIndex: number): boolean {
    const syncState = this.pttSyncStates.get(tableIndex);
    const isLoading = this.pttSyncLoading.get(tableIndex);
    const enabled = !isLoading && syncState && !syncState.isCurrent;

    // Only log major state changes to prevent infinite loops
    const stateKey = `${tableIndex}_${syncState?.isCurrent}_${isLoading}`;

    if (!this._loggedSyncStates?.has(stateKey)) {
      if (!this._loggedSyncStates) this._loggedSyncStates = new Set();
      this._loggedSyncStates.add(stateKey);
    }

    return enabled;
  }

  /**
   * Check if PTT is out of sync (disabled for editing)
   * Returns true if PTT needs sync (user must sync before editing)
   */
  isPTTOutOfSync(tableIndex: number): boolean {
    const syncState = this.pttSyncStates.get(tableIndex);
    return syncState && !syncState.isCurrent;
  }

  isPTTSyncLoading(tableIndex: number): boolean {
    const loading = this.pttSyncLoading.get(tableIndex) || false;
    // Only log if loading state changes
    if (this._lastLoadingState?.[tableIndex] !== loading) {
      if (!this._lastLoadingState) this._lastLoadingState = {};
      this._lastLoadingState[tableIndex] = loading;
    }
    return loading;
  }

  // Methods to handle Nominal Corrosion Allowance editing
  startEditCorrosionAllowance(): void {
    // Instead of using a text input, open the service selection modal with Corrosion Allowance data
    // Load Corrosion Allowance options from API using getDistinctActiveValues
    this.esraApiClient.getDistinctActiveValues('nomCorAllow').subscribe({
      next: (propertyValues) => {
        // New endpoint returns string[] directly — no .map() needed
        const availableCorrosionAllowanceFromAPI = (propertyValues ?? [])
          .map((value: string, index: number) => ({
            id: `corrosion-allowance-${index}`,
            name: value || 'Unknown Corrosion Allowance',
            code: `CA-${index}`,
            description: `Corrosion Allowance: ${value || 'Unknown'}`,
            selected: false
          }));

        // Get currently selected Corrosion Allowance (single selection for field editing)
        const currentlySelectedCorrosionAllowance: ServiceOption[] = [];
        if (this.pipeClassViewModel.nominalCorrosionAllowance) {
          // Find the current Corrosion Allowance in the available options or create a representation
          const currentCorrosionAllowance = availableCorrosionAllowanceFromAPI.find(ca =>
            ca.name === this.pipeClassViewModel.nominalCorrosionAllowance?.value
          );

          if (currentCorrosionAllowance) {
            currentlySelectedCorrosionAllowance.push({...currentCorrosionAllowance, selected: true});
          } else {
            // If current Corrosion Allowance is not in API list, create a representation
            currentlySelectedCorrosionAllowance.push({
              id: 'current-corrosion-allowance',
              name: this.pipeClassViewModel.nominalCorrosionAllowance?.value,
              code: 'CA-CURRENT',
              description: 'Current Corrosion Allowance',
              selected: true
            });
          }
        }

        // Open modal for single Corrosion Allowance selection
        const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
          width: '95vw',
          height: '90vh',
          maxWidth: '1200px',
          maxHeight: '90vh',
          minWidth: '300px',
          minHeight: '450px',
          disableClose: true,
          panelClass: ['service-selection-dialog', 'responsive-modal'],
          data: {
            availableServices: availableCorrosionAllowanceFromAPI, // Reusing ServiceOption interface
            selectedServices: currentlySelectedCorrosionAllowance,
            singleSelection: true, // Single selection mode for field editing
            title: 'Select Nominal Corrosion Allowance', // Custom title for Corrosion Allowance selection
            context: 'field-edit', // Context for field editing
          }
        });

        // Handle modal result
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.length > 0) {
            this.updateCorrosionAllowanceFromModal(result[0]); // Get first (and only) selected Corrosion Allowance
          }
        });
      },
      error: (error) => {
        // Error loading options
      }
    });
  }

  /**
   * Updates the Nominal Corrosion Allowance field with the selected Corrosion Allowance from the modal
   */
  private updateCorrosionAllowanceFromModal(selectedCorrosionAllowance: ServiceOption): void {
    if (selectedCorrosionAllowance) {
      // Create PipeClassPropertyViewModel if it doesn't exist
      if (!this.pipeClassViewModel.nominalCorrosionAllowance) {
        this.pipeClassViewModel.nominalCorrosionAllowance = new PipeClassPropertyViewModel({
          name: 'Nominal Corrosion Allowance',
          value: selectedCorrosionAllowance.name,
          commonNotes: []
        });
      } else {
        this.pipeClassViewModel.nominalCorrosionAllowance.value = selectedCorrosionAllowance.name;
      }

      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  // Service edit methods
  startEditService(): void {
    // Instead of using a text input, open the service selection modal
    // Load services from API first
    this.esraApiClient.getAllCurrentServices().subscribe(apiServices => {
      this.allAvailableServices = apiServices || [];

      const availableServicesFromAPI = this.allAvailableServices
        .map((service, index) => {
          const serviceId = service.id?.toString() || `service-${index}`;
          const serviceName = service.name || 'Unknown Service';
          const serviceCode = serviceName.substring(0, 50);
          const serviceDescription = service.updated
            ? `Updated: ${this.formatServiceDate(service.updated)}`
            : 'Service from API';

          return {
            id: serviceId,
            name: serviceName,
            code: serviceCode,
            description: serviceDescription,
            selected: false
          };
        });

      // Get currently selected service (single selection for field editing)
      const currentlySelectedService: ServiceOption[] = [];
      if (this.pipeClassViewModel.service?.name) {
        // Find the current service in the available services or create a representation
        const currentService = availableServicesFromAPI.find(s =>
          s.name === this.pipeClassViewModel.service.name
        );

        if (currentService) {
          currentlySelectedService.push({...currentService, selected: true});
        } else {
          // If current service is not in API list, create a representation
          currentlySelectedService.push({
            id: this.pipeClassViewModel.service.id?.toString() || 'current',
            name: this.pipeClassViewModel.service.name,
            code: `SVC-${this.pipeClassViewModel.service.id || 'current'}`,
            description: 'Current service',
            selected: true
          });
        }
      }

      if (availableServicesFromAPI.length === 0) {
        // No services available
        return;
      }

      // Open modal for single service selection
      const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
        width: '95vw',
        height: '90vh',
        maxWidth: '1200px',
        maxHeight: '90vh',
        minWidth: '300px',
        minHeight: '450px',
        disableClose: true,
        panelClass: ['service-selection-dialog', 'responsive-modal'],
        data: {
          availableServices: availableServicesFromAPI,
          selectedServices: currentlySelectedService,
          singleSelection: true, // New property to indicate single selection mode
          title: 'Select Service', // Custom title for field editing
          context: 'field-edit' // Context for field editing
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && Array.isArray(result) && result.length > 0) {
          // Update the service field with the selected service (single selection)
          const selectedService = result[0]; // Take only the first selected service
          this.updateServiceFromModal(selectedService);
        }
      });
    });
  }

  /**
   * Updates the service field from modal selection (single service)
   */
  private updateServiceFromModal(selectedService: ServiceOption): void {
    if (selectedService) {
      this.pipeClassViewModel.service = new ServiceViewModel({
        id: selectedService.id,
        name: selectedService.name,
        updated: new Date()
      });

      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  // Temperature Limit edit methods
  startEditTemperatureLimit(): void {
    this.isEditingTemperatureLimit = true;
    this.temperatureLimitForm.patchValue({
      minimumTemperature: this.pipeClassViewModel.temperatureRange?.minimum,
      maximumTemperature: this.pipeClassViewModel.temperatureRange?.maximum
    });
  }

  saveTemperatureLimit(): void {
    if (this.temperatureLimitForm.valid) {
      const minValue = this.temperatureLimitForm.value.minimumTemperature;
      const maxValue = this.temperatureLimitForm.value.maximumTemperature;

      // Create temperatureRange if it doesn't exist
      if (!this.pipeClassViewModel.temperatureRange) {
        this.pipeClassViewModel.temperatureRange = new TemperatureRangeViewModel();
      }

      // Update the view model
      this.pipeClassViewModel.temperatureRange.minimum = minValue.toString(); // Store as string
      this.pipeClassViewModel.temperatureRange.maximum = maxValue.toString();

      // Mark summary table as changed
      this.markSummaryTableChanged();

      // Notify parent component about the change
      this.propertyChanged.emit({
        field: 'temperatureRange',
        value: this.pipeClassViewModel.temperatureRange,
        section: 'summary'
      });

      this.isEditingTemperatureLimit = false;

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  cancelEditTemperatureLimit(): void {
    this.isEditingTemperatureLimit = false;
    this.temperatureLimitForm.reset();
  }

  // Rating Class edit methods - now modal-based
  startEditRatingClass(): void {

    let currentValue = '';
    let currentEdition = '2020'; // Default value when no edition is stored

    if (this.pipeClassViewModel.ratingClass) {
      const ratingClass = this.pipeClassViewModel.ratingClass as any;
      if (ratingClass.value !== undefined) {
        currentValue = ratingClass.value;
        if (ratingClass.edition) {
          currentEdition = ratingClass.edition;
        }
      } else {
        currentValue = this.pipeClassViewModel.ratingClass.value || '';
      }
    }

    const dialogRef = this.dialog.open(PropertySelectionModalComponent, {
      panelClass: 'wide-modal',
      data: {
        currentValue: currentValue,
        currentEdition: currentEdition,
        title: 'Select Rating Class',
        propertyType: 'RatingClass',
        editionFieldLabel: 'Edition',
        searchFieldLabel: 'Search',
      } as PropertySelectionData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.value) {

        const newRatingClass = new RatingClassViewModel({
          value: result.value,
          edition: result.edition || '2020',
          commonNotes: this.pipeClassViewModel.ratingClass?.commonNotes || []
        });

        this.pipeClassViewModel.ratingClass = newRatingClass;

        // Mark summary table as changed
        this.markSummaryTableChanged();

        // Notify parent component about the change
        this.propertyChanged.emit({
          field: 'ratingClass',
          value: newRatingClass,
          section: 'summary'
        });

        // Force change detection to refresh the UI immediately
        this.cdr.detectChanges();
      }
    });
  }


  getRatingClassValue(): string {
    if (!this.pipeClassViewModel.ratingClass) return '';

    const ratingClass = this.pipeClassViewModel.ratingClass as any;

    if (ratingClass.value !== undefined) {
      return ratingClass.value;
    }

    return this.pipeClassViewModel.ratingClass.value || '';
  }

  getRatingClassEdition(): string {
    if (!this.pipeClassViewModel.ratingClass) return '';

    const ratingClass = this.pipeClassViewModel.ratingClass as any;

    return ratingClass.edition || '2020';
  }

  getDesignCodeValue(): string {
    if (!this.pipeClassViewModel.designCode) return '';

    const designCode = this.pipeClassViewModel.designCode as any;
    if (designCode.value !== undefined) {
      return designCode.value;
    }
    return this.pipeClassViewModel.designCode.value || '';
  }

  getDesignCodeEdition(): string {
    if (!this.pipeClassViewModel.designCode) return '';

    const designCode = this.pipeClassViewModel.designCode as any;
    return designCode.edition || '2020';
  }

  /**
   * Returns the Nominal Corrosion Allowance display value for the given pipe class view model.
   *
   * Accepts an optional `vm` parameter so the same logic can serve both the current pipe class
   * and a previous version (compare mode). Call signatures:
   * - `getNominalCorrosionAllowanceValue()` — uses `this.pipeClassViewModel` (normal display).
   * - `getNominalCorrosionAllowanceValue(previousPipeClassViewModel)` — computes the value for
   *   the previous version; pass directly as `[previousValue]` on `app-revision-field`.
   *
   * Returns `undefined` (not `''`) when `vm` is explicitly `null` — this signals to
   * `RevisionFieldComponent` that no previous data has been loaded yet, so it suppresses the diff
   * highlight rather than showing a false positive.
   *
   * The `as any` cast handles both the current `PipeClassPropertyViewModel` structure
   * (`{ value: string }`) and the legacy plain-string field that pre-dates the view model refactor.
   *
   * @param vm The pipe class view model to read from. Omit to use the current view model.
   * @returns The corrosion allowance string, `''` when absent, or `undefined` when `vm` is `null`.
   */
  getNominalCorrosionAllowanceValue(vm?: PipeClassViewModel | null): string | undefined {
    if (vm === null) return undefined;
    const _vm: PipeClassViewModel = vm ?? this.pipeClassViewModel;
    if (!_vm?.nominalCorrosionAllowance) return '';
    const corrosionAllowance = _vm.nominalCorrosionAllowance as any;
    // Handle both new PipeClassPropertyViewModel structure and old string structure
    if (corrosionAllowance.value !== undefined) {
      return corrosionAllowance.value || '';
    }
    // Fallback for old structure (direct string)
    return corrosionAllowance || '';
  }

  /**
   * Returns the Limited By display value for the given pipe class view model.
   *
   * Accepts an optional `vm` parameter so the same logic can serve both the current pipe class
   * and a previous version (compare mode). Call signatures:
   * - `getLimitedByValue()` — uses `this.pipeClassViewModel` (normal display).
   * - `getLimitedByValue(previousPipeClassViewModel)` — computes the value for the previous
   *   version; pass directly as `[previousValue]` on `app-revision-field`.
   *
   * Returns `undefined` (not `''`) when `vm` is explicitly `null` — this signals to
   * `RevisionFieldComponent` that no previous data has been loaded yet, so it suppresses the diff
   * highlight rather than showing a false positive.
   *
   * The `as any` cast handles both the current `PipeClassPropertyViewModel` structure
   * (`{ value: string }`) and the legacy plain-string field that pre-dates the view model refactor.
   *
   * @param vm The pipe class view model to read from. Omit to use the current view model.
   * @returns The limited-by string, `''` when absent, or `undefined` when `vm` is `null`.
   */
  getLimitedByValue(vm?: PipeClassViewModel | null): string | undefined {
    if (vm === null) return undefined;
    const _vm: PipeClassViewModel = vm ?? this.pipeClassViewModel;
    if (!_vm?.limitedBy) return '';
    const limitedBy = _vm.limitedBy as any;
    // Handle both new PipeClassPropertyViewModel structure and old string structure
    if (limitedBy.value !== undefined) {
      return limitedBy.value || '';
    }
    // Fallback for old structure (direct string)
    return limitedBy || '';
  }

  /**
   * Get minimum temperature value from TemperatureRangeViewModel structure
   */
  getMinimumTemperature(): string {
    if (!this.pipeClassViewModel.temperatureRange) return '';

    const tempRange = this.pipeClassViewModel.temperatureRange as any;
    return tempRange.minimum || '';
  }

  /**
   * Get maximum temperature value from TemperatureRangeViewModel structure
   */
  getMaximumTemperature(): string {
    if (!this.pipeClassViewModel.temperatureRange) return '';

    const tempRange = this.pipeClassViewModel.temperatureRange as any;
    return tempRange.maximum || '';
  }

  /**
   * Get temperature unit from TemperatureRangeViewModel structure
   */
  getTemperatureUnit(): string {
    if (!this.pipeClassViewModel.temperatureRange) return '°F';

    const tempRange = this.pipeClassViewModel.temperatureRange as any;
    return tempRange.temperatureUnit || '°F';
  }

  /**
   * Returns the Rating Class display string (value + edition) for the given pipe class view model.
   *
   * Accepts an optional `vm` parameter so the same formatting logic serves both the current pipe
   * class and a previous version (compare mode). Call signatures:
   * - `getRatingClassDisplay()` — uses `this.pipeClassViewModel` (normal display).
   * - `getRatingClassDisplay(previousPipeClassViewModel)` — computes the display for the previous
   *   version; pass directly as `[previousValue]` on `app-revision-field`.
   *
   * Returns `undefined` (not `''`) when `vm` is explicitly `null` — this signals to
   * `RevisionFieldComponent` that no previous data has been loaded yet, so it suppresses the diff
   * highlight rather than showing a false positive.
   *
   * The `'2020'` edition fallback is intentionally identical for both the current and previous
   * path — if both sides have an empty edition string the formatted output will be the same,
   * avoiding a false-positive diff (e.g., `"1500 RF (2020)"` vs `"1500 RF (2020)"`).
   *
   * @param vm The pipe class view model to read from. Omit to use the current view model.
   * @returns A formatted string like `"1500 RF (2020)"`, `''` when absent, or `undefined` when `vm` is `null`.
   */
  getRatingClassDisplay(vm?: PipeClassViewModel | null): string | undefined {
    if (vm === null) return undefined;
    const _vm: PipeClassViewModel = vm ?? this.pipeClassViewModel;
    if (!_vm?.ratingClass) return '';
    const ratingClass = _vm.ratingClass as any;
    const val: string = ratingClass.value !== undefined ? ratingClass.value : (_vm.ratingClass.value || '');
    // Use the same '2020' fallback for both current and previous so identical data is never flagged as changed
    const edition: string = ratingClass.edition || '2020';
    return edition ? `${val} (${edition})` : val;
  }

  /**
   * Returns the Design Code display string (value + edition) for the given pipe class view model.
   *
   * Accepts an optional `vm` parameter so the same formatting logic serves both the current pipe
   * class and a previous version (compare mode). Call signatures:
   * - `getDesignCodeDisplay()` — uses `this.pipeClassViewModel` (normal display).
   * - `getDesignCodeDisplay(previousPipeClassViewModel)` — computes the display for the previous
   *   version; pass directly as `[previousValue]` on `app-revision-field`.
   *
   * Returns `undefined` (not `''`) when `vm` is explicitly `null` — this signals to
   * `RevisionFieldComponent` that no previous data has been loaded yet, so it suppresses the diff
   * highlight rather than showing a false positive.
   *
   * The `'2020'` edition fallback is intentionally identical for both the current and previous
   * path — if both sides have an empty edition string the formatted output will be the same,
   * avoiding a false-positive diff.
   *
   * @param vm The pipe class view model to read from. Omit to use the current view model.
   * @returns A formatted string like `"ASME B31.3 (2020)"`, `''` when absent, or `undefined` when `vm` is `null`.
   */
  getDesignCodeDisplay(vm?: PipeClassViewModel | null): string | undefined {
    if (vm === null) return undefined;
    const _vm: PipeClassViewModel = vm ?? this.pipeClassViewModel;
    if (!_vm?.designCode) return '';
    const designCode = _vm.designCode as any;
    const val: string = designCode.value !== undefined ? designCode.value : (_vm.designCode.value || '');
    // Use the same '2020' fallback for both current and previous so identical data is never flagged as changed
    const edition: string = designCode.edition || '2020';
    return edition ? `${val} (${edition})` : val;
  }

  /**
   * Returns the temperature range display string for the given pipe class view model.
   *
   * Accepts an optional `vm` parameter so the same formatting logic serves both the current pipe
   * class and a previous version (compare mode). Call signatures:
   * - `getTemperatureDisplay()` — uses `this.pipeClassViewModel` (normal display).
   * - `getTemperatureDisplay(previousPipeClassViewModel)` — computes the display for the previous
   *   version; pass directly as `[previousValue]` on `app-revision-field`.
   *
   * Returns `undefined` (not `''`) when `vm` is explicitly `null` — this signals to
   * `RevisionFieldComponent` that no previous data has been loaded yet, so it suppresses the diff
   * highlight rather than showing a false positive.
   *
   * @param vm The pipe class view model to read from. Omit to use the current view model.
   * @returns A formatted string like `"from -20°F to 750°F"`, `''` when absent, or `undefined` when `vm` is `null`.
   */
  getTemperatureDisplay(vm?: PipeClassViewModel | null): string | undefined {
    if (vm === null) return undefined;
    const _vm: PipeClassViewModel = vm ?? this.pipeClassViewModel;
    if (!_vm?.temperatureRange) return '';
    const tempRange = _vm.temperatureRange as any;
    const min: string = tempRange.minimum || '';
    const max: string = tempRange.maximum || '';
    const unit: string = tempRange.temperatureUnit || '°F';
    return `from ${min}${unit} to ${max}${unit}`;
  }

  // ── P&T compare helpers ──────────────────────────────────────────────────────

  /**
   * Returns the P&T table from the previous pipe class at the same positional index as the
   * current table, or `null` when no previous data is available for that index.
   *
   * Tables are matched by array index (not by ID or name), consistent with the cell-by-cell
   * positional comparison rule: column `i` in the current table is always compared to column
   * `i` in the previous table at the same slot.
   *
   * @param tableIndex Index into `pipeClassViewModel.pressureTemperatureTables`.
   */
  getPreviousPTT(tableIndex: number): PressureTemperatureTableViewModel | null {
    return this.previousPipeClassViewModel?.pressureTemperatureTables?.[tableIndex] || null;
  }

  // ── List compare getters (used by app-revision-list-field) ──────────────────

  /**
   * Current general note text strings for the list compare component.
   * Mapped from `generalNotesList` which is the live, editable list.
   */
  get currentGeneralNoteTexts(): string[] {
    return this.generalNotesList.map(n => n.text);
  }

  /**
   * Previous general note text strings for compare mode.
   * Returns `undefined` when `previousPipeClassViewModel` is `null` (not yet loaded), which
   * signals `RevisionListFieldComponent` to suppress diff highlights during loading.
   */
  get previousGeneralNoteTexts(): string[] | undefined {
    if (this.previousPipeClassViewModel === null) { return undefined; }
    return (this.previousPipeClassViewModel.customNotes || []).map(n => n.note || '');
  }

  /** Current service names for the list compare component. */
  get currentServiceNames(): string[] {
    return (this.currentServicesList as ServiceViewModel[]).map(s => s.name || '');
  }

  /**
   * Previous service names for compare mode.
   * Returns `undefined` when `previousPipeClassViewModel` is `null` (not yet loaded).
   */
  get previousServiceNames(): string[] | undefined {
    if (this.previousPipeClassViewModel === null) { return undefined; }
    return (this.previousPipeClassViewModel.serviceDetails || []).map(s => s.name || '');
  }

  // ── PTT notes compare helpers ─────────────────────────────────────────────

  /**
   * Returns the hydrotest note from the previous revision's PTT table at `tableIndex`.
   * Uses the same first-available-test-note priority as `getPTTHydrotestNote`.
   * Returns `undefined` when previous data has not been loaded yet (suppresses the diff).
   */
  getPreviousPTTHydrotestNote(tableIndex: number): string | undefined {
    if (this.previousPipeClassViewModel === null) { return undefined; }
    const table: PressureTemperatureTableViewModel | undefined =
      this.previousPipeClassViewModel?.pressureTemperatureTables?.[tableIndex];
    if (!table) { return ''; }
    const note: PressureTemperatureTableNoteViewModel | undefined =
      table.testNotes?.find(n => !!n.note) ?? table.relationTestNotes?.find(n => !!n.note);
    return note?.note ?? '';
  }

  /** Current PTT additional note texts mapped for use with `app-revision-list-field`. */
  getPTTAdditionalNoteTexts(tableIndex: number): string[] {
    return this.getPTTAdditionalNotes(tableIndex).map(n => n.text);
  }

  /**
   * Previous PTT additional note texts for compare mode.
   * Combines `additionalNotes` and `relationAdditionalNotes` from the previous VM's table.
   * Returns `undefined` when previous data has not been loaded yet (suppresses the diff).
   */
  getPreviousPTTAdditionalNoteTexts(tableIndex: number): string[] | undefined {
    if (this.previousPipeClassViewModel === null) { return undefined; }
    const table: PressureTemperatureTableViewModel | undefined =
      this.previousPipeClassViewModel?.pressureTemperatureTables?.[tableIndex];
    if (!table) { return []; }
    return [
      ...(table.additionalNotes || []).map(n => n.note || ''),
      ...(table.relationAdditionalNotes || []).map(n => n.note || '')
    ].filter(s => !!s);
  }

  /** Current PTT common note codes mapped for use with `app-revision-list-field`. */
  getPTTCommonNoteCodes(tableIndex: number): string[] {
    return this.getPTTCommonNotes(tableIndex).map(n => n.code);
  }

  /**
   * Previous PTT common note codes for compare mode.
   * Combines `commonNotes` and `relationCommonNotes` from the previous VM's table.
   * Returns `undefined` when previous data has not been loaded yet (suppresses the diff).
   */
  getPreviousPTTCommonNoteCodes(tableIndex: number): string[] | undefined {
    if (this.previousPipeClassViewModel === null) { return undefined; }
    const table: PressureTemperatureTableViewModel | undefined =
      this.previousPipeClassViewModel?.pressureTemperatureTables?.[tableIndex];
    if (!table) { return []; }
    return [
      ...(table.commonNotes || []).map(n => n.codeId || n.note || ''),
      ...(table.relationCommonNotes || []).map(n => n.codeId || n.note || '')
    ].filter(s => !!s);
  }

  // ── Property-field common notes compare helpers ───────────────────────────

  /**
   * Extracts display codes from a `NoteOption` array for use with `app-revision-list-field`.
   * The `code` field on `NoteOption` already holds the canonical database `codeId` — it is
   * mapped from `CommonNoteViewModel.codeId` in `getNotesForProperty`.
   */
  getNoteCodes(notes: NoteOption[]): string[] {
    return notes.map(n => n.code || '').filter(s => !!s);
  }

  /**
   * Returns the note codes for a named scalar property on the previous pipe class.
   * Returns `undefined` when previous data has not been loaded yet (suppresses the diff).
   *
   * @param propertyName The camelCase property name on `PipeClassViewModel` (e.g., `'service'`, `'material'`).
   */
  getPreviousPropertyNoteCodes(propertyName: string): string[] | undefined {
    if (this.previousPipeClassViewModel === null) { return undefined; }
    const property: any = (this.previousPipeClassViewModel as any)[propertyName];
    if (!property?.commonNotes) { return []; }
    return (property.commonNotes as CommonNoteViewModel[])
      .map(n => n.codeId || n.note || '')
      .filter(s => !!s);
  }



  saveRatingClass(): void {
    if (this.ratingClassForm.valid) {
      const newValue = this.ratingClassForm.value.ratingClass;

      if (this.pipeClassViewModel.ratingClass) {
        this.pipeClassViewModel.ratingClass.value = newValue;
      }

      this.isEditingRatingClass = false;
    }
  }

  cancelEditRatingClass(): void {
    this.isEditingRatingClass = false;
    this.ratingClassForm.reset();
  }

  // Material edit methods
  startEditMaterial(): void {
    // Load material options from the dictionary via the new active-values endpoint
    this.esraApiClient.getDistinctActiveValues('material').subscribe({
      next: (propertyValues) => {
        // New endpoint returns string[] directly — no .map() needed
        const availableMaterialsFromAPI = (propertyValues ?? [])
          .map((value: string, index: number) => ({
            id: `material-${index}`,
            name: value || 'Unknown Material',
            code: `MAT-${index}`,
            description: `Material: ${value || 'Unknown'}`,
            selected: false
          }));

        // Get currently selected material (single selection for field editing)
        const currentlySelectedMaterial: ServiceOption[] = [];
        if (this.pipeClassViewModel.material?.value) {
          const currentMaterial = availableMaterialsFromAPI.find(m =>
            m.name === this.pipeClassViewModel.material.value
          );

          if (currentMaterial) {
            currentlySelectedMaterial.push({...currentMaterial, selected: true});
          } else {
            currentlySelectedMaterial.push({
              id: 'current-material',
              name: this.pipeClassViewModel.material.value,
              code: 'MAT-CURRENT',
              description: 'Current material',
              selected: true
            });
          }
        }

        // Open modal for single material selection
        const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
          width: '95vw',
          height: '90vh',
          maxWidth: '1200px',
          maxHeight: '90vh',
          minWidth: '300px',
          minHeight: '450px',
          disableClose: true,
          panelClass: ['service-selection-dialog', 'responsive-modal'],
          data: {
            availableServices: availableMaterialsFromAPI,
            selectedServices: currentlySelectedMaterial,
            singleSelection: true,
            title: 'Select Material',
            context: 'field-edit',
          }
        });

        // Handle modal result
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.length > 0) {
            this.updateMaterialFromModal(result[0]);
          }
        });
      },
      error: () => {
        // Error loading materials — modal stays closed
      }
    });
  }

  /**
   * Updates the material field with the selected material from the modal
   */
  private updateMaterialFromModal(selectedMaterial: ServiceOption): void {
    if (selectedMaterial) {
      if (!this.pipeClassViewModel.material) {
        this.pipeClassViewModel.material = new PipeClassPropertyViewModel();
      }
      this.pipeClassViewModel.material.value = selectedMaterial.name;

      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  // Design Code edit methods - now modal-based
  startEditDesignCode(): void {
    const dialogRef = this.dialog.open(PropertySelectionModalComponent, {
      panelClass: 'wide-modal',
      data: {
        currentValue: this.pipeClassViewModel.designCode?.value || '',
        title: 'Select Design Code',
        propertyType: 'DesignCode',
        editionFieldLabel: 'Edition',
        searchFieldLabel: 'Search'
      } as PropertySelectionData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.value) {
        const newDesignCode = new DesignCodeViewModel({
          value: result.value,
          edition: result.edition || '2020',
          commonNotes: this.pipeClassViewModel.designCode?.commonNotes || []
        });

        this.pipeClassViewModel.designCode = newDesignCode;

        // Mark summary table as changed
        this.markSummaryTableChanged();

        // Notify parent component about the change
        this.propertyChanged.emit({
          field: 'designCode',
          value: newDesignCode,
          section: 'summary'
        });

        // Force change detection to refresh the UI immediately
        this.cdr.detectChanges();
      }
    });
  }

  saveDesignCode(): void {
    if (this.designCodeForm.valid) {
      const newValue = this.designCodeForm.value.designCode;
      this.pipeClassViewModel.designCode.value = newValue;
      this.isEditingDesignCode = false;
    }
  }

  cancelEditDesignCode(): void {
    this.isEditingDesignCode = false;
    this.designCodeForm.reset();
  }

  // Valve Trim edit methods - now using modal
  startEditValveTrim(): void {
    // Instead of using a text input, open the service selection modal with Valve Trim data
    // Load Valve Trim options from API using getDistinctActiveValues
    this.esraApiClient.getDistinctActiveValues('valveTrim').subscribe({
      next: (propertyValues) => {
        // New endpoint returns string[] directly — no .map() needed
        const availableValveTrimFromAPI = (propertyValues ?? [])
          .map((value: string, index: number) => ({
            id: `valve-trim-${index}`,
            name: value || 'Unknown Valve Trim',
            code: `VT-${index}`,
            description: `Valve Trim: ${value || 'Unknown'}`,
            selected: false
          }));

        // Get currently selected Valve Trim (single selection for field editing)
        const currentlySelectedValveTrim: ServiceOption[] = [];
        if (this.pipeClassViewModel.valveTrim?.value) {
          // Find the current Valve Trim in the available options or create a representation
          const currentValveTrim = availableValveTrimFromAPI.find(vt =>
            vt.name === this.pipeClassViewModel.valveTrim.value
          );

          if (currentValveTrim) {
            currentlySelectedValveTrim.push({...currentValveTrim, selected: true});
          } else {
            // If current Valve Trim is not in API list, create a representation
            currentlySelectedValveTrim.push({
              id: 'current-valve-trim',
              name: this.pipeClassViewModel.valveTrim.value,
              code: 'VT-CURRENT',
              description: 'Current Valve Trim',
              selected: true
            });
          }
        }

        // Open modal for single Valve Trim selection
        const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
          width: '95vw',
          height: '90vh',
          maxWidth: '1200px',
          maxHeight: '90vh',
          minWidth: '300px',
          minHeight: '450px',
          disableClose: true,
          panelClass: ['service-selection-dialog', 'responsive-modal'],
          data: {
            availableServices: availableValveTrimFromAPI, // Reusing ServiceOption interface
            selectedServices: currentlySelectedValveTrim,
            singleSelection: true, // Single selection mode for field editing
            title: 'Select Valve Trim', // Custom title for Valve Trim selection
            context: 'field-edit', // Context for field editing
          }
        });

        // Handle modal result
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.length > 0) {
            this.updateValveTrimFromModal(result[0]); // Get first (and only) selected Valve Trim
          }
        });
      },
      error: (error) => {
        // Error loading Valve Trim options
      }
    });
  }

  /**
   * Updates the Valve Trim field with the selected Valve Trim from the modal
   */
  private updateValveTrimFromModal(selectedValveTrim: ServiceOption): void {
    if (selectedValveTrim) {
      if (!this.pipeClassViewModel.valveTrim) {
        this.pipeClassViewModel.valveTrim = new PipeClassPropertyViewModel();
      }
      this.pipeClassViewModel.valveTrim.value = selectedValveTrim.name;

      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  // Limited By edit methods
  startEditLimitedBy(): void {
    // Instead of using a text input, open the service selection modal with Limited By data
    // Load Limited By options from API using getDistinctActiveValues
    this.esraApiClient.getDistinctActiveValues('limitedBy').subscribe({
      next: (propertyValues) => {
        // New endpoint returns string[] directly — no .map() needed
        const availableLimitedByFromAPI = (propertyValues ?? [])
          .map((value: string, index: number) => ({
            id: `limited-by-${index}`,
            name: value || 'Unknown Limited By',
            code: `LB-${index}`,
            description: `Limited By: ${value || 'Unknown'}`,
            selected: false
          }));

        // Get currently selected Limited By (single selection for field editing)
        const currentlySelectedLimitedBy: ServiceOption[] = [];
        if (this.pipeClassViewModel.limitedBy) {
          // Find the current Limited By in the available options or create a representation
          const currentLimitedBy = availableLimitedByFromAPI.find(lb =>
            lb.name === this.pipeClassViewModel.limitedBy?.value
          );

          if (currentLimitedBy) {
            currentlySelectedLimitedBy.push({...currentLimitedBy, selected: true});
          } else {
            // If current Limited By is not in API list, create a representation
            currentlySelectedLimitedBy.push({
              id: 'current-limited-by',
              name: this.pipeClassViewModel.limitedBy?.value,
              code: 'LB-CURRENT',
              description: 'Current Limited By',
              selected: true
            });
          }
        }

        // Open modal for single Limited By selection
        const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
          width: '95vw',
          height: '90vh',
          maxWidth: '1200px',
          maxHeight: '90vh',
          minWidth: '300px',
          minHeight: '450px',
          disableClose: true,
          panelClass: ['service-selection-dialog', 'responsive-modal'],
          data: {
            availableServices: availableLimitedByFromAPI, // Reusing ServiceOption interface
            selectedServices: currentlySelectedLimitedBy,
            singleSelection: true, // Single selection mode for field editing
            title: 'Select Limited By', // Custom title for Limited By selection
            context: 'field-edit', // Context for field editing
          }
        });

        // Handle modal result
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.length > 0) {
            this.updateLimitedByFromModal(result[0]); // Get first (and only) selected Limited By
          }
        });
      },
      error: (error) => {
        // Error loading Limited By options
      }
    });
  }

  /**
   * Updates the Limited By field with the selected Limited By from the modal
   */
  private updateLimitedByFromModal(selectedLimitedBy: ServiceOption): void {
    if (selectedLimitedBy) {
      // Create PipeClassPropertyViewModel if it doesn't exist
      if (!this.pipeClassViewModel.limitedBy) {
        this.pipeClassViewModel.limitedBy = new PipeClassPropertyViewModel({
          name: 'Limited By',
          value: selectedLimitedBy.name,
          commonNotes: []
        });
      } else {
        this.pipeClassViewModel.limitedBy.value = selectedLimitedBy.name;
      }

      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  // Examination edit methods - now using modal
  startEditExamination(): void {
    // Instead of using a text input, open the service selection modal with Examination data
    // Load Examination options from API using getDistinctActiveValues
    this.esraApiClient.getDistinctActiveValues('examination').subscribe({
      next: (propertyValues) => {
        // New endpoint returns string[] directly — no .map() needed
        const availableExaminationFromAPI = (propertyValues ?? [])
          .map((value: string, index: number) => ({
            id: `examination-${index}`,
            name: value || 'Unknown Examination',
            code: `EX-${index}`,
            description: `Examination: ${value || 'Unknown'}`,
            selected: false
          }));

        // Get currently selected Examination (single selection for field editing)
        const currentlySelectedExamination: ServiceOption[] = [];
        if (this.pipeClassViewModel.examination?.value) {
          // Find the current Examination in the available options or create a representation
          const currentExamination = availableExaminationFromAPI.find(ex =>
            ex.name === this.pipeClassViewModel.examination.value
          );

          if (currentExamination) {
            currentlySelectedExamination.push({...currentExamination, selected: true});
          } else {
            // If current Examination is not in API list, create a representation
            currentlySelectedExamination.push({
              id: 'current-examination',
              name: this.pipeClassViewModel.examination.value,
              code: 'EX-CURRENT',
              description: 'Current Examination',
              selected: true
            });
          }
        }

        // Open modal for single Examination selection
        const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
          width: '95vw',
          height: '90vh',
          maxWidth: '1200px',
          maxHeight: '90vh',
          minWidth: '300px',
          minHeight: '450px',
          disableClose: true,
          panelClass: ['service-selection-dialog', 'responsive-modal'],
          data: {
            availableServices: availableExaminationFromAPI, // Reusing ServiceOption interface
            selectedServices: currentlySelectedExamination,
            singleSelection: true, // Single selection mode for field editing
            title: 'Select Examination', // Custom title for Examination selection
            context: 'field-edit', // Context for field editing
          }
        });

        // Handle modal result
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.length > 0) {
            this.updateExaminationFromModal(result[0]); // Get first (and only) selected Examination
          }
        });
      },
      error: (error) => {
        // Error loading Examination options
      }
    });
  }

  /**
   * Updates the Examination field with the selected Examination from the modal
   */
  private updateExaminationFromModal(selectedExamination: ServiceOption): void {
    if (selectedExamination) {
      if (!this.pipeClassViewModel.examination) {
        this.pipeClassViewModel.examination = new PipeClassPropertyViewModel();
      }
      this.pipeClassViewModel.examination.value = selectedExamination.name;

      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  // Stress Relief edit methods - now using modal
  startEditStressRelief(): void {
    // Instead of using a text input, open the service selection modal with Stress Relief data
    // Load Stress Relief options from API using getDistinctActiveValues
    this.esraApiClient.getDistinctActiveValues('stressRelief').subscribe({
      next: (propertyValues) => {
        // New endpoint returns string[] directly — no .map() needed
        const availableStressReliefFromAPI = (propertyValues ?? [])
          .map((value: string, index: number) => ({
            id: `stress-relief-${index}`,
            name: value || 'Unknown Stress Relief',
            code: `SR-${index}`,
            description: `Stress Relief: ${value || 'Unknown'}`,
            selected: false
          }));

        // Get currently selected Stress Relief (single selection for field editing)
        const currentlySelectedStressRelief: ServiceOption[] = [];
        if (this.pipeClassViewModel.stressRelief?.value) {
          // Find the current Stress Relief in the available options or create a representation
          const currentStressRelief = availableStressReliefFromAPI.find(sr =>
            sr.name === this.pipeClassViewModel.stressRelief.value
          );

          if (currentStressRelief) {
            currentlySelectedStressRelief.push({...currentStressRelief, selected: true});
          } else {
            // If current Stress Relief is not in API list, create a representation
            currentlySelectedStressRelief.push({
              id: 'current-stress-relief',
              name: this.pipeClassViewModel.stressRelief.value,
              code: 'SR-CURRENT',
              description: 'Current Stress Relief',
              selected: true
            });
          }
        }

        // Open modal for single Stress Relief selection
        const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
          width: '95vw',
          height: '90vh',
          maxWidth: '1200px',
          maxHeight: '90vh',
          minWidth: '300px',
          minHeight: '450px',
          disableClose: true,
          panelClass: ['service-selection-dialog', 'responsive-modal'],
          data: {
            availableServices: availableStressReliefFromAPI, // Reusing ServiceOption interface
            selectedServices: currentlySelectedStressRelief,
            singleSelection: true, // Single selection mode for field editing
            title: 'Select Stress Relief', // Custom title for Stress Relief selection
            context: 'field-edit', // Context for field editing
          }
        });

        // Handle modal result
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.length > 0) {
            this.updateStressReliefFromModal(result[0]); // Get first (and only) selected Stress Relief
          }
        });
      },
      error: (error) => {
        // Error loading Stress Relief options
      }
    });
  }

  /**
   * Updates the Stress Relief field with the selected Stress Relief from the modal
   */
  private updateStressReliefFromModal(selectedStressRelief: ServiceOption): void {
    if (selectedStressRelief) {
      if (!this.pipeClassViewModel.stressRelief) {
        this.pipeClassViewModel.stressRelief = new PipeClassPropertyViewModel();
      }
      this.pipeClassViewModel.stressRelief.value = selectedStressRelief.name;

      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  startAddNewService(): void {
    // Load services from API first
    this.esraApiClient.getAllCurrentServiceDetails().subscribe(apiServices => {
      this.allAvailableServices = apiServices || [];

      const availableServicesFromAPI = this.allAvailableServices
        .map((service, index) => {
          const serviceId = service.id?.toString() || `service-${index}`;
          const serviceName = service.name || 'Unknown Service';
          const serviceCode = serviceName.substring(0, 50);
          const serviceDescription = service.updated
            ? `Updated: ${this.formatServiceDate(service.updated)}`
            : 'Service from API';

          return {
            id: serviceId,
            name: serviceName,
            code: serviceCode,
            description: serviceDescription,
            selected: false
          };
        });

      // Get currently selected services from temporary changes list and pipe class
      const currentSelectedServices = this.getCurrentSelectedServicesAsOptions();

      // Use only API services - no fallback to test data
      const availableServices = availableServicesFromAPI;

      if (availableServices.length === 0) {
        // No services available
        return;
      }

      // Open modal with available services and current selections
      const dialogRef = this.dialog.open(ServiceSelectionModalComponent, {
        width: '95vw',
        height: '90vh',
        maxWidth: '1200px',
        maxHeight: '90vh',
        minWidth: '300px',
        minHeight: '450px',
        disableClose: true,
        panelClass: ['service-selection-dialog', 'responsive-modal'],
        data: {
          availableServices: availableServices,
          selectedServices: currentSelectedServices,
          singleSelection: false, // Multiple selection for list management
          title: 'Add Services', // Custom title for list management
          context: 'list-management' // Context for adding services to list
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && Array.isArray(result)) {
          this.updateServicesFromModal(result);
        }
      });
    });
  }

  /**
   * Gets currently selected services as ServiceOption array
   */
  private getCurrentSelectedServicesAsOptions(): ServiceOption[] {
    const selectedOptions: ServiceOption[] = [];

    // Use pipeClassViewModel.serviceDetails directly
    if (this.pipeClassViewModel.serviceDetails && this.pipeClassViewModel.serviceDetails.length > 0) {
      this.pipeClassViewModel.serviceDetails.forEach((service, index) => {
        const serviceOption = {
          id: service.id?.toString() || `service-${index}`,
          name: service.name || '',
          code: `SVC-${service.id || index}`,
          description: service.name || `Service ${index + 1}`,
          selected: true
        };

        selectedOptions.push(serviceOption);
      });
    }

    return selectedOptions;
  }

  /**
   * Updates the services from modal selection
   */
  private updateServicesFromModal(selectedServices: ServiceOption[]): void {
    // Initialize serviceDetails if it doesn't exist
    if (!this.pipeClassViewModel.serviceDetails) {
      this.pipeClassViewModel.serviceDetails = [];
    }

    // Clear existing services and add selected ones
    this.pipeClassViewModel.serviceDetails = [];

    selectedServices.forEach((serviceOption) => {
      const service = new ServiceViewModel({
        id: serviceOption.id,
        name: serviceOption.name,
        updated: new Date()
      });

      this.pipeClassViewModel.serviceDetails.push(service);
    });

    // Sort descending alphabetically to match the API and PDF output order
    this.pipeClassViewModel.serviceDetails.sort((a, b) => (b.name || '').localeCompare(a.name || ''));

    this.markSummaryTableChanged();
    this.cdr.detectChanges();
  }

  cancelAddNewService(): void {
    this.isAddingNewService = false;
    this.newServiceForm.reset();
    // No need to reset services changes since we work directly with pipeClassViewModel
  }

  removeService(serviceIndex: number): void {
    if (!this.pipeClassViewModel.serviceDetails) {
      return;
    }

    if (serviceIndex >= 0 && serviceIndex < this.pipeClassViewModel.serviceDetails.length) {
      this.pipeClassViewModel.serviceDetails.splice(serviceIndex, 1);
      this.markSummaryTableChanged();
      this.cdr.detectChanges();
    }
  }

  get currentServicesList(): any[] {
    const services: any[] = this.pipeClassViewModel.serviceDetails || [];
    return [...services].sort((a, b) => (b.name || '').localeCompare(a.name || ''));
  }

  startAddNewNotes(): void {
    // Get current common notes from pipeClassViewModel
    const currentNotes = this.getCurrentCommonNotes();

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Common Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) { // Handle both empty array and filled array
        const previousNotes = [...currentNotes];
        this.updateCommonNotes(result);
        this.cdr.detectChanges();

        // Determine which notes were added and which were removed
        const addedNotes = result.filter(newNote =>
          !previousNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
        );

        const removedNotes = previousNotes.filter(oldNote =>
          !result.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
        );

        // Emit added notes
        if (addedNotes.length > 0) {
          this.notesAddedEvent.emit({
            notes: addedNotes,
            origin: 'summary-common'
          });
        }

        // Emit removed notes
        if (removedNotes.length > 0) {
          this.notesRemovedEvent.emit({
            notes: removedNotes,
            origin: 'summary-common'
          });
        }
      }
    });
  }

  removeNote(noteIndex: number): void {
    const currentNotes = this.getCurrentCommonNotes();
    if (noteIndex >= 0 && noteIndex < currentNotes.length) {
      currentNotes.splice(noteIndex, 1);
      this.updateCommonNotes(currentNotes);
      this.cdr.detectChanges();
    }
  }

  // General Notes CRUD Methods
  startAddNewGeneralNote(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minHeight: '400px',
      data: {
        title: 'Add General Note',
        message: 'Enter your note text:',
        showInput: true,
        useTextarea: true,
        inputPlaceholder: 'Enter note text...',
        confirmText: 'Add Note',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.trim()) {
        const newNote: GeneralNote = {
          id: this.generateNoteId(),
          text: result.trim(),
          createdDate: new Date()
        };
        this.generalNotesList.push(newNote);
        this.saveGeneralNotes();
        this.cdr.detectChanges();
      }
    });
  }

  editGeneralNote(index: number): void {
    if (index >= 0 && index < this.generalNotesList.length) {
      const noteToEdit = this.generalNotesList[index];

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '700px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        minHeight: '400px',
        data: {
          title: 'Edit General Note',
          message: 'Edit your note text:',
          showInput: true,
          useTextarea: true,
          inputPlaceholder: 'Enter note text...',
          inputValue: noteToEdit.text,
          confirmText: 'Update Note',
          cancelText: 'Cancel'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && result.trim()) {
          this.generalNotesList[index] = {
            ...noteToEdit,
            text: result.trim(),
            modifiedDate: new Date()
          };
          this.saveGeneralNotes();
          this.cdr.detectChanges();
        }
      });
    }
  }

  removeGeneralNote(index: number): void {
    if (index >= 0 && index < this.generalNotesList.length) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Remove General Note',
          message: 'Are you sure you want to remove this note?',
          confirmText: 'Remove',
          cancelText: 'Cancel'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.generalNotesList.splice(index, 1);
          this.saveGeneralNotes();
          this.cdr.detectChanges();
        }
      });
    }
  }

  private generateNoteId(): string {
    return this.generateGuid(); // Use proper GUID instead of custom string
  }

  private saveGeneralNotes(): void {
        // Convert GeneralNote format to PipeClassNoteViewModel and update pipeClassViewModel
    if (!this.pipeClassViewModel.customNotes) {
      this.pipeClassViewModel.customNotes = [];
    }

    // Convert GeneralNote to PipeClassNoteViewModel (only id and note properties exist)
    this.pipeClassViewModel.customNotes = this.generalNotesList.map(note => {
      // Ensure ID is a valid GUID
      const validId = this.isValidGuid(note.id) ? note.id : this.generateGuid();

      return new PipeClassNoteViewModel({
        id: validId,
        note: note.text
      });
    });

    // Emit event to parent component to notify about the change and trigger API update
    this.generalNotesChangedEvent.emit({
      notes: this.generalNotesList,
      pipeClassId: this.pipeClassViewModel?.id
    });

    // Also emit propertyChanged event for consistency with other properties
    this.propertyChanged.emit({
      field: 'customNotes',
      value: this.pipeClassViewModel.customNotes,
      section: 'summary'
    });
  }

  private loadGeneralNotes(): void {
    // Load general notes from pipeClassViewModel.customNotes

    if (this.pipeClassViewModel?.customNotes && this.pipeClassViewModel.customNotes.length > 0) {
      // Convert PipeClassNoteViewModel to GeneralNote format
      this.generalNotesList = this.pipeClassViewModel.customNotes.map((note, index) => ({
        id: note.id || this.generateGuid(), // Use proper GUID when ID is missing
        text: note.note || '',
        createdDate: new Date(), // PipeClassNoteViewModel doesn't have createdDate
        modifiedDate: new Date()  // PipeClassNoteViewModel doesn't have modifiedDate
      }));

    } else {
      this.generalNotesList = [];
    }
  }

  get currentNotesList(): NoteOption[] {
    return this.getCurrentCommonNotes();
  }

  get currentStressReliefNotesList(): NoteOption[] {
    return this.getNotesForProperty('stressRelief');
  }

  get currentRatingClassNotesList(): NoteOption[] {
    return this.getNotesForProperty('ratingClass');
  }

  get currentValveTrimNotesList(): NoteOption[] {
    return this.getNotesForProperty('valveTrim');
  }

  get currentMaterialNotesList(): NoteOption[] {
    return this.getNotesForProperty('material');
  }

  get currentExaminationNotesList(): NoteOption[] {
    return this.getNotesForProperty('examination');
  }

  get currentServiceNotesList(): NoteOption[] {
    const notes = this.getNotesForProperty('service');
    return notes;
  }

  get currentTemperatureLimitNotesList(): NoteOption[] {
    return this.getNotesForProperty('temperatureRange');
  }

  get currentDesignCodeNotesList(): NoteOption[] {
    return this.getNotesForProperty('designCode');
  }

  get currentLimitedByNotesList(): NoteOption[] {
    return this.getNotesForProperty('limitedBy');
  }

  get currentCorrosionAllowanceNotesList(): NoteOption[] {
    return this.getNotesForProperty('nominalCorrosionAllowance');
  }

  // ========== PTT NOTES METHODS ==========

  /**
   * Initialize PTT notes arrays for each pressure temperature table
   */
  private initializePTTNotes(): void {
    if (!this.pipeClassViewModel?.pressureTemperatureTables) {
      return;
    }

    // If already initialized and arrays have the correct length, don't reinitialize (preserves user edits)
    const tableCount = this.pipeClassViewModel.pressureTemperatureTables.length;
    if (this.pttNotesInitialized &&
        this.pttHydrotestNotes.length >= tableCount &&
        this.pttAdditionalNotes.length >= tableCount &&
        this.pttNotesChanges.length >= tableCount) {
      return;
    }

    // Mark as initialized to prevent reinitializing on subsequent calls
    this.pttNotesInitialized = true;

    // Ensure we have an array for each PTT table
    while (this.pttNotesChanges.length < tableCount) {
      const tableIndex = this.pttNotesChanges.length;
      const table = this.pipeClassViewModel.pressureTemperatureTables[tableIndex];
      const notesMap = new Map<string, NoteOption>();

      // Add RelationCommonNotes first
      if (table.relationCommonNotes && table.relationCommonNotes.length > 0) {
        table.relationCommonNotes.forEach((note, index) => {
          if (note.codeId) {
            notesMap.set(note.codeId, {
              id: note.noteId || `ptt-relation-${tableIndex}-${index}`,
              code: note.codeId,
              note: note.note || '',
              category: `ptt-relation-${tableIndex}`,
              selected: true
            });
          }
        });
      }

      // Add CommonNotes (these override RelationCommonNotes if same code)
      if (table.commonNotes && table.commonNotes.length > 0) {
        table.commonNotes.forEach((note, index) => {
          if (note.codeId) {
            notesMap.set(note.codeId, {
              id: note.noteId || `ptt-common-${tableIndex}-${index}`,
              code: note.codeId,
              note: note.note || '',
              category: `ptt-common-${tableIndex}`,
              selected: true
            });
          }
        });
      }

      this.pttNotesChanges.push(Array.from(notesMap.values()));
    }

    // Ensure we have a hydrotest note string for each PTT table
    // IMPORTANT: Only initialize missing values, don't overwrite existing user edits
    for (let i = this.pttHydrotestNotes.length; i < tableCount; i++) {
      const currentIndex = i;

      // Only initialize if this is a completely new entry (array is growing)
      // Initialize with relationTestNotes first (editable), then fallback to testNotes (read-only)
      const table = this.pipeClassViewModel.pressureTemperatureTables[currentIndex];

      // Process relation notes using helper function
      const processedNotes = processRelationNotes(table);

      // CLEAN UP DUPLICATES: Remove duplicate notes
      if (table?.relationTestNotes) {
        const originalCount = table.relationTestNotes.length;
        const filteredNotes = table.relationTestNotes;

        // Then remove duplicates based on note content (keep only the first occurrence)
        const uniqueNotes = new Map();
        filteredNotes.forEach(note => {
          const key = note.note?.trim() || '';
          if (key && !uniqueNotes.has(key)) {
            uniqueNotes.set(key, note);
          }
        });

        table.relationTestNotes = Array.from(uniqueNotes.values());

      }

      // Find the FIRST test note with type PtrNote (hydrotest info) for the textbox
      // Priority: PtrNote (hydrotest) > Test > any other type
      const firstRelationTestNote = table?.relationTestNotes?.find(note =>
        note.type === 'PtrNote' || note.type === 'ptrNote' || note.type === 'ptrnote'
      ) || table?.relationTestNotes?.find(note =>
        note.type === 'Test' || note.type === 'test'
      ) || table?.relationTestNotes?.[0]; // Fallback to first note

      const firstExistingTestNote = table?.testNotes?.find(note =>
        note.type === 'PtrNote' || note.type === 'ptrNote' || note.type === 'ptrnote'
      ) || table?.testNotes?.find(note =>
        note.type === 'Test' || note.type === 'test'
      ) || table?.testNotes?.[0]; // Fallback to first note

      // Use the first available note - prioritize relation notes
      const initialHydrotestNote = firstRelationTestNote?.note || firstExistingTestNote?.note || '';


      // Push the initial note for this new table index
      this.pttHydrotestNotes.push(initialHydrotestNote);
    }

    for (let i = this.pttAdditionalNotes.length; i < tableCount; i++) {
      const table = this.pipeClassViewModel.pressureTemperatureTables[i];

      // Debug logging for additional notes
      console.log('Processing table for additional notes:', {
        tableIndex: i,
        table: table,
        relationAdditionalNotes: table?.relationAdditionalNotes,
        additionalNotes: table?.additionalNotes
      });

      // Process relation notes using helper function
      const processedNotes = processRelationNotes(table);

      const existingAdditionalNotes = table?.additionalNotes?.map(note => ({
        text: note.note || '',
        isEditable: false, // These are read-only
        source: 'existing' as const
      })) || [];

      const relationAdditionalNotes = processedNotes.relationAdditionalNotes?.map(note => ({
        text: note.note || '',
        isEditable: true, // These can be edited
        source: 'relation' as const
      })) || [];

      const unifiedNotes = [...existingAdditionalNotes, ...relationAdditionalNotes];
      this.pttAdditionalNotes.push(unifiedNotes);
    }


    while (this.pttCommonNotes.length < tableCount) {
      const tableIndex = this.pttCommonNotes.length;
      const table = this.pipeClassViewModel.pressureTemperatureTables[tableIndex];
      const existingCommonNotes = table?.commonNotes?.map((note, idx) => {
        return {
          id: note.noteId || '',
          code: note.codeId || '',
          note: note.note || '',
          isEditable: false,
          source: 'existing' as const
        };
      }) || [];


      const existingNoteCodes = new Set(existingCommonNotes.map(note => note.code));

      const relationCommonNotes = table?.relationCommonNotes?.filter(note => {
        return note.codeId && !existingNoteCodes.has(note.codeId);
      }).map(note => ({
        id: note.noteId || '',
        code: note.codeId || '',
        note: note.note || '',
        isEditable: true,
        source: 'relation' as const
      })) || [];


      const unifiedCommonNotes = [...existingCommonNotes, ...relationCommonNotes];
      this.pttCommonNotes.push(unifiedCommonNotes);
    }

    while (this.pttTestNotes.length < tableCount) {
      const table = this.pipeClassViewModel.pressureTemperatureTables[this.pttTestNotes.length];

      const existingTestNotes = table?.testNotes?.map(note => ({
        note: note.note || '',
        type: note.type || '',
        isEditable: false,
        source: 'existing' as const
      })) || [];

      const relationTestNotes = table?.relationTestNotes?.map(note => ({
        note: note.note || '',
        type: note.type || '',
        isEditable: true,
        source: 'relation' as const
      })) || [];

      const unifiedTestNotes = [...existingTestNotes, ...relationTestNotes];
      this.pttTestNotes.push(unifiedTestNotes);
    }

    if (this.pttNotesChanges.length > tableCount) {
      this.pttNotesChanges.length = tableCount;
    }
    if (this.pttHydrotestNotes.length > tableCount) {
      this.pttHydrotestNotes.length = tableCount;
    }
    if (this.pttAdditionalNotes.length > tableCount) {
      this.pttAdditionalNotes.length = tableCount;
    }
    if (this.pttCommonNotes.length > tableCount) {
      this.pttCommonNotes.length = tableCount;
    }
    if (this.pttTestNotes.length > tableCount) {
      this.pttTestNotes.length = tableCount;
    }
  }

  getPTTNotes(tableIndex: number): NoteOption[] {
    const unifiedNotes = this.getPTTCommonNotes(tableIndex);
    return unifiedNotes.map(note => ({
      id: note.id,
      code: note.code,
      note: note.note,
      category: 'Common',
      selected: true
    }));
  }


  canRemovePTTNote(table: PressureTemperatureTableViewModel, noteIndex: number): boolean {
    return false;
  }

  startAddPTTNotes(tableIndex: number): void {
    this.initializePTTNotes();

    const tableName = this.pipeClassViewModel.pressureTemperatureTables[tableIndex]?.name ||
                      `Pressure/Temperature Table ${tableIndex + 1}`;

    // Get current unified common notes and convert to NoteOption format for modal
    const currentUnifiedNotes = this.getPTTCommonNotes(tableIndex);
    const currentNotes: NoteOption[] = currentUnifiedNotes.map(note => ({
      id: note.id,
      code: note.code,
      note: note.note,
      category: 'Common',
      selected: true,
      // Mark notes from CommonNotes as read-only (cannot be deselected)
      readonly: !note.isEditable
    }));

    // Separate read-only and editable notes for better UX
    const readOnlyNotes = currentNotes.filter(note => note.readonly);

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: `Select Common Notes for ${tableName}`,
        allowRemove: true, // Enable removal of editable notes
        readOnlyNotes: readOnlyNotes, // Pass read-only notes separately
        subtitle: readOnlyNotes.length > 0 ?
          `Note: ${readOnlyNotes.length} existing note(s) from CommonNotes cannot be modified here` : undefined
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && Array.isArray(result)) {
        // Process the result to update unified common notes
        this.processPTTCommonNotesChanges(tableIndex, currentUnifiedNotes, result);
        this.markPTTChanged(tableIndex);
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Process common notes changes from modal, updating the unified array
   */
  private processPTTCommonNotesChanges(tableIndex: number, previousNotes: UnifiedCommonNote[], selectedNotes: NoteOption[]): void {
    if (tableIndex < 0 || tableIndex >= this.pttCommonNotes.length) {
      return;
    }

    // Find which notes were added and which were removed
    const previousNotesMap = new Map(previousNotes.map(note => [note.code, note]));
    // Start with existing read-only notes (can't be removed)
    const readOnlyNotes = previousNotes.filter(note => !note.isEditable);

    // Add selected editable notes (both existing and new)
    const editableNotes: UnifiedCommonNote[] = selectedNotes
      .filter(note => {
        // Only include if it's not already a read-only note
        return !readOnlyNotes.some(readonly => readonly.code === note.code);
      })
      .map(note => {
        // Check if this was previously an editable note or if it's new
        const existingNote = previousNotesMap.get(note.code);
        return {
          id: note.id,
          code: note.code,
          note: note.note,
          isEditable: true,
          source: existingNote?.source === 'relation' ? 'relation' : 'new'
        } as UnifiedCommonNote;
      });

    // Combine read-only and editable notes
    this.pttCommonNotes[tableIndex] = [...readOnlyNotes, ...editableNotes];
    const table = this.pipeClassViewModel?.pressureTemperatureTables?.[tableIndex];
    if (table) {
      table.relationCommonNotes = editableNotes.map(note => new CommonNoteViewModel({
        noteId: note.id,
        codeId: note.code,
        note: note.note,
        isActive: true,
        isArchived: false,
        isReferenced: false,
        updated: new Date()
      }));
    }

    // Emit events to parent for bidirectional synchronization
    // Find added notes (exist in selected but not in previous editable notes)
    const previousEditableNotes = previousNotes.filter(n => n.isEditable);
    const addedNotes = selectedNotes.filter(selected =>
      !previousEditableNotes.some(prev => prev.code === selected.code && prev.id === selected.id)
    );

    // Find removed notes (exist in previous editable but not in selected)
    const removedNotes = previousEditableNotes.filter(prev =>
      !selectedNotes.some(selected => selected.code === prev.code && selected.id === prev.id)
    );

    // Emit added notes event
    if (addedNotes.length > 0) {
      const notesToAdd = addedNotes.map(note => ({
        noteId: note.id,
        codeId: note.code,
        note: note.note
      }));
      this.notesAddedEvent.emit({
        notes: notesToAdd,
        origin: `ptt-common-notes-${tableIndex}`
      });
    }

    // Emit removed notes event
    if (removedNotes.length > 0) {
      const notesToRemove = removedNotes.map(note => ({
        noteId: note.id,
        codeId: note.code,
        note: note.note
      }));
      this.notesRemovedEvent.emit({
        notes: notesToRemove,
        origin: `ptt-common-notes-${tableIndex}`
      });
    }
  }

  /**
   * Remove a specific note from a PTT table
   */
  removePTTNote(tableIndex: number, noteIndex: number): void {
    const table = this.pipeClassViewModel?.pressureTemperatureTables?.[tableIndex];
    if (!table) {
      return;
    }

    const relationNotesCount = table.relationCommonNotes?.length || 0;

    // If noteIndex is within relationCommonNotes range, it's an original note (can't delete)
    if (noteIndex < relationNotesCount) {
      console.warn('Cannot remove original relation notes');
      return;
    }

    // Calculate the index within commonNotes (our added notes)
    const commonNoteIndex = noteIndex - relationNotesCount;

    if (table.commonNotes && commonNoteIndex >= 0 && commonNoteIndex < table.commonNotes.length) {
      table.commonNotes.splice(commonNoteIndex, 1);
      this.markPTTChanged(tableIndex);
      this.cdr.detectChanges();

    }
  }

  /**
   * Ensure PTT arrays have minimum required size
   */
  private ensurePTTArraySize(minSize: number): void {
    while (this.pttHydrotestNotes.length < minSize) {
      this.pttHydrotestNotes.push('');
    }
    while (this.pttAdditionalNotes.length < minSize) {
      this.pttAdditionalNotes.push([]);
    }
    while (this.pttNotesChanges.length < minSize) {
      this.pttNotesChanges.push([]);
    }
  }

  /**
   * Update hydrotest note for a specific PTT table
   */
  updatePTTHydrotestNote(tableIndex: number, note: string): void {
    console.log('=== UPDATE HYDROTEST NOTE ===');
    console.log('tableIndex:', tableIndex);
    console.log('new note value:', `"${note}"`);
    console.log('current pttHydrotestNotes array:', this.pttHydrotestNotes);
    console.log('Stack trace:', new Error().stack);

    // Ensure array is large enough for this index
    this.ensurePTTArraySize(tableIndex + 1);

    if (tableIndex >= 0) {
      this.pttHydrotestNotes[tableIndex] = note;
      console.log('Updated pttHydrotestNotes array:', this.pttHydrotestNotes);

      // Update the relationTestNote in the pipeClassViewModel so it gets saved when user saves
      this.updateRelationTestNoteInViewModel(tableIndex, note);

      this.markPTTChanged(tableIndex);
      this.cdr.detectChanges();
    }
  }

  /**
   * Update the relationTestNote in the pipeClassViewModel so changes persist when user saves entity
   */
  private updateRelationTestNoteInViewModel(tableIndex: number, note: string): void {
    if (!this.pipeClassViewModel?.pressureTemperatureTables?.[tableIndex]) {
      return;
    }

    const table = this.pipeClassViewModel.pressureTemperatureTables[tableIndex];

    // Ensure relationTestNotes array exists
    if (!table.relationTestNotes) {
      table.relationTestNotes = [];
    }

    table.relationTestNotes = table.relationTestNotes.filter(n =>
      n.type !== 'Test' && n.type !== 'HydrotestNote'
    );

    // Create exactly ONE new hydrotest note (only if note has content)
    if (note && note.trim()) {
      const hydrotestNote = new PressureTemperatureTableNoteViewModel({
        type: 'Test',
        note: note
      });
      table.relationTestNotes.push(hydrotestNote);
    }
  }

  /**
   * Get hydrotest note for a specific PTT table
   */
  getPTTHydrotestNote(tableIndex: number): string {
    // Ensure arrays are large enough for this index
    this.ensurePTTArraySize(tableIndex + 1);

    // If this index doesn't have a value yet, try to initialize it from the table data ONLY ONCE
    if (!this.pttHydrotestNotes[tableIndex] && this.pipeClassViewModel?.pressureTemperatureTables?.[tableIndex]) {
      const table = this.pipeClassViewModel.pressureTemperatureTables[tableIndex];

      // Get the FIRST note for THIS table only - avoid duplicates from multiple tables
      let singleNote = '';

      // First, check relation test notes for this specific table
      if (table.relationTestNotes && table.relationTestNotes.length > 0) {
        const testNote = table.relationTestNotes.find(note =>
          note.type === 'HydrotestNote' ||
          note.type === 'Test' ||
          (note.note && note.note.toLowerCase().includes('hydro'))
        );
        if (testNote) {
          singleNote = testNote.note || '';
        }
      }

      // If not found, check existing test notes for this specific table
      if (!singleNote && table.testNotes && table.testNotes.length > 0) {
        const testNote = table.testNotes.find(note =>
          note.type === 'HydrotestNote' ||
          note.type === 'Test' ||
          (note.note && note.note.toLowerCase().includes('hydro'))
        );
        if (testNote) {
          singleNote = testNote.note || '';
        }
      }
      // Set ONLY the single note for this table - no accumulation
      this.pttHydrotestNotes[tableIndex] = singleNote;
    }

    const value = this.pttHydrotestNotes[tableIndex] || '';
    // Removed excessive logging to prevent infinite loops
    return value;
  }

  // ========== PTT ADDITIONAL NOTES METHODS ==========

  /**
   * Get additional notes for a specific PTT table
   */
  getPTTAdditionalNotes(tableIndex: number): UnifiedAdditionalNote[] {
    // Don't initialize here - only return already initialized data
    return this.pttAdditionalNotes[tableIndex] || [];
  }

  /**
   * Add a new additional note to a specific PTT table
   */
  addPTTAdditionalNote(tableIndex: number, note: string): void {
    if (!note || note.trim() === '') {
      return;
    }

    this.initializePTTNotes();
    if (tableIndex >= 0 && tableIndex < this.pttAdditionalNotes.length) {
      if (!this.pttAdditionalNotes[tableIndex]) {
        this.pttAdditionalNotes[tableIndex] = [];
      }
      const unifiedNote: UnifiedAdditionalNote = {
        text: note.trim(),
        isEditable: true,
        source: 'new'
      };
      this.pttAdditionalNotes[tableIndex].push(unifiedNote);
      this.markPTTChanged(tableIndex);
      this.cdr.detectChanges();
    }
  }

  /**
   * Remove an additional note from a specific PTT table
   */
  removePTTAdditionalNote(tableIndex: number, noteIndex: number): void {
    // Don't re-initialize on remove - just work with existing data
    if (tableIndex >= 0 && tableIndex < this.pttAdditionalNotes.length) {
      const notes = this.pttAdditionalNotes[tableIndex];
      if (notes && noteIndex >= 0 && noteIndex < notes.length) {
        notes.splice(noteIndex, 1);
        this.markPTTChanged(tableIndex);
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Update an existing additional note in a specific PTT table
   */
  updatePTTAdditionalNote(tableIndex: number, noteIndex: number, newNote: string): void {
    if (!newNote || newNote.trim() === '') {
      return;
    }

    // Don't re-initialize on update - just work with existing data
    if (tableIndex >= 0 && tableIndex < this.pttAdditionalNotes.length) {
      const notes = this.pttAdditionalNotes[tableIndex];
      if (notes && noteIndex >= 0 && noteIndex < notes.length) {
        notes[noteIndex].text = newNote.trim();
        this.markPTTChanged(tableIndex);
        this.cdr.detectChanges();
      }
    }
  }

  // ========== PTT COMMON NOTES METHODS ==========

  /**
   * Get unified common notes for a specific PTT table
   */
  getPTTCommonNotes(tableIndex: number): UnifiedCommonNote[] {
    // Don't initialize here - only return already initialized data
    return this.pttCommonNotes[tableIndex] || [];
  }

  /**
   * Remove a common note from a specific PTT table (only editable notes)
   */
  removePTTCommonNote(tableIndex: number, noteIndex: number): void {
    // Don't re-initialize on remove - just work with existing data
    if (tableIndex >= 0 && tableIndex < this.pttCommonNotes.length) {
      const notes = this.pttCommonNotes[tableIndex];
      if (notes && noteIndex >= 0 && noteIndex < notes.length) {
        const note = notes[noteIndex];
        // Only allow removal of editable notes
        if (note.isEditable) {
          notes.splice(noteIndex, 1);

          const table = this.pipeClassViewModel?.pressureTemperatureTables?.[tableIndex];
          if (table) {
            const editableNotes = notes.filter(n => n.isEditable);
            table.relationCommonNotes = editableNotes.map(n => new CommonNoteViewModel({
              noteId: n.id,
              codeId: n.code,
              note: n.note,
              isActive: true,
              isArchived: false,
              isReferenced: false,
              updated: new Date()
            }));
          }

          // Emit removal event to parent for unified list synchronization
          this.notesRemovedEvent.emit({
            notes: [{
              noteId: note.id,
              codeId: note.code,
              note: note.note
            }],
            origin: `ptt-common-notes-${tableIndex}`
          });

          this.markPTTChanged(tableIndex);
          this.cdr.detectChanges();
        }
      }
    }
  }

  /**
   * Public method to get PTT common notes for save (only editable notes)
   */
  public getPTTCommonNotesForSave(tableIndex: number): {noteId: string, codeId: string, note: string}[] {
    const unifiedNotes = this.getPTTCommonNotes(tableIndex);
    // Return only the editable notes (new or modified) for the API
    return unifiedNotes
      .filter(note => note.isEditable)
      .map(note => ({
        noteId: note.id,
        codeId: note.code,
        note: note.note
      }));
  }

  /**
   * Get unified test notes for a specific PTT table
   */
  getPTTTestNotes(tableIndex: number): UnifiedTestNote[] {
    // Don't initialize here - only return already initialized data
    return this.pttTestNotes[tableIndex] || [];
  }

  /**
   * Remove a test note from a specific PTT table (only editable notes)
   */
  removePTTTestNote(tableIndex: number, noteIndex: number): void {
    // Don't re-initialize on remove - just work with existing data
    if (tableIndex >= 0 && tableIndex < this.pttTestNotes.length) {
      const notes = this.pttTestNotes[tableIndex];
      if (notes && noteIndex >= 0 && noteIndex < notes.length) {
        const note = notes[noteIndex];
        // Only allow removal of editable notes
        if (note.isEditable) {
          notes.splice(noteIndex, 1);
          this.markPTTChanged(tableIndex);
          this.cdr.detectChanges();
        }
      }
    }
  }

  /**
   * Add a new test note to a specific PTT table
   */
  addPTTTestNote(tableIndex: number, noteText: string): void {
    this.initializePTTNotes();
    if (tableIndex >= 0 && tableIndex < this.pttTestNotes.length && noteText.trim()) {
      const newNote: UnifiedTestNote = {
        note: noteText.trim(),
        type: 'TestNote',
        isEditable: true,
        source: 'new'
      };
      this.pttTestNotes[tableIndex].push(newNote);
      this.markPTTChanged(tableIndex);
      this.cdr.detectChanges();
    }
  }

  /**
   * Public method to get PTT test notes for save (only editable notes)
   */
  public getPTTTestNotesForSave(tableIndex: number): {note: string, type: string}[] {
    const unifiedNotes = this.getPTTTestNotes(tableIndex);
    // Return only the editable notes (new or modified) for the API
    return unifiedNotes
      .filter(note => note.isEditable)
      .map(note => ({
        note: note.note,
        type: note.type || 'TestNote'
      }));
  }

  /**
   * Start editing an existing additional note
   */
  startEditPTTAdditionalNote(tableIndex: number, noteIndex: number): void {
    const notes = this.getPTTAdditionalNotes(tableIndex);
    if (noteIndex >= 0 && noteIndex < notes.length) {
      this.additionalNoteEditState = {
        tableIndex,
        noteIndex,
        isEditing: true,
        editText: notes[noteIndex].text
      };
      this.cdr.detectChanges();
    }
  }

  /**
   * Save the edited additional note
   */
  saveEditPTTAdditionalNote(): void {
    if (this.additionalNoteEditState && this.additionalNoteEditState.editText?.trim()) {
      this.updatePTTAdditionalNote(
        this.additionalNoteEditState.tableIndex,
        this.additionalNoteEditState.noteIndex,
        this.additionalNoteEditState.editText
      );
      this.additionalNoteEditState = null;
    }
  }

  /**
   * Cancel editing additional note
   */
  cancelEditPTTAdditionalNote(): void {
    this.additionalNoteEditState = null;
    this.cdr.detectChanges();
  }

  /**
   * Start adding a new additional note
   */
  startAddPTTAdditionalNote(tableIndex: number): void {
    if (!this.newAdditionalNoteText[tableIndex]) {
      this.newAdditionalNoteText[tableIndex] = '';
    }
    this.cdr.detectChanges();
  }

  /**
   * Save the new additional note
   */
  savePTTAdditionalNote(tableIndex: number): void {
    const noteText = this.newAdditionalNoteText[tableIndex];
    if (noteText && noteText.trim()) {
      this.addPTTAdditionalNote(tableIndex, noteText);
      this.newAdditionalNoteText[tableIndex] = ''; // Clear the input
    }
  }

  /**
   * Cancel adding new additional note
   */
  cancelPTTAdditionalNote(tableIndex: number): void {
    this.newAdditionalNoteText[tableIndex] = '';
    this.cdr.detectChanges();
  }

  /**
   * Check if currently editing an additional note
   */
  isEditingAdditionalNote(tableIndex: number, noteIndex: number): boolean {
    return this.additionalNoteEditState?.tableIndex === tableIndex &&
           this.additionalNoteEditState?.noteIndex === noteIndex &&
           this.additionalNoteEditState?.isEditing === true;
  }

  /**
   * Check if currently adding a new additional note for a table
   */
  isAddingAdditionalNote(tableIndex: number): boolean {
    return this.newAdditionalNoteText.hasOwnProperty(tableIndex) &&
           this.newAdditionalNoteText[tableIndex] !== undefined;
  }

  /**
   * Remove/Delete a specific PTT table
   */
  removePTTTable(tableIndex: number): void {
    if (!this.pipeClassViewModel?.pressureTemperatureTables) {
      return;
    }

    if (tableIndex >= 0 && tableIndex < this.pipeClassViewModel.pressureTemperatureTables.length) {
      const notesToCheck: CommonNoteViewModel[] = [];

      if (this.pttCommonNotes[tableIndex]) {
        const editableNotes = this.pttCommonNotes[tableIndex]
          .filter(note => note.isEditable)
          .map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note,
            isActive: true,
            isArchived: false,
            isReferenced: false,
            updated: new Date()
          }));

        notesToCheck.push(...editableNotes);
      }

      // Remove the table from the main array
      this.pipeClassViewModel.pressureTemperatureTables.splice(tableIndex, 1);

      // Also remove corresponding notes, hydrotest notes, test notes, common notes and additional notes
      if (tableIndex < this.pttNotesChanges.length) {
        this.pttNotesChanges.splice(tableIndex, 1);
      }
      if (tableIndex < this.pttHydrotestNotes.length) {
        this.pttHydrotestNotes.splice(tableIndex, 1);
      }
      if (tableIndex < this.pttAdditionalNotes.length) {
        this.pttAdditionalNotes.splice(tableIndex, 1);
      }
      if (tableIndex < this.pttCommonNotes.length) {
        this.pttCommonNotes.splice(tableIndex, 1);
      }
      if (tableIndex < this.pttTestNotes.length) {
        this.pttTestNotes.splice(tableIndex, 1);
      }

      // Emit removal event for parent to check if these notes are orphaned
      if (notesToCheck.length > 0) {
        this.notesRemovedEvent.emit({
          notes: notesToCheck,
          origin: `ptt-table-removed-${tableIndex}`
        });
      }

      this.cdr.detectChanges();
    }
  }

  // ========== PTT MODAL METHODS ==========

  /**
   * Opens the Add/Replace Pressure Temperature Table modal
   */
  openAddPTTDialog(): void {
    const existingTables = (this.pipeClassViewModel?.pressureTemperatureTables || []).map((table, index) => {
    const clonedTable = PressureTemperatureTableViewModel.fromJS(table.toJSON());

      if (this.pttCommonNotes[index]) {
        const editableNotes = this.pttCommonNotes[index]
          .filter(note => note.isEditable)
          .map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note,
            isActive: true,
            isArchived: false,
            isReferenced: false,
            updated: new Date()
          }));
        clonedTable.relationCommonNotes = editableNotes;
      }

      if (this.pttTestNotes[index]) {
        const editableTestNotes = this.pttTestNotes[index]
          .filter(note => note.isEditable)
          .map(note => new PressureTemperatureTableNoteViewModel({
            note: note.note,
            type: note.type || ''
          }));
        clonedTable.relationTestNotes = editableTestNotes;
      }

      if (this.pttAdditionalNotes[index]) {
        const additionalNotes = this.pttAdditionalNotes[index]
          .filter(note => note.isEditable)
          .map(note => new PressureTemperatureTableNoteViewModel({
            note: note.text,
            type: 'Additional'
          }));
        clonedTable.relationAdditionalNotes = additionalNotes;
      }

      return clonedTable;
    });

    const modalData: PressureTempTableModalData = {
      existingTables: existingTables
    };

    modalData.existingTables.forEach((t, idx) => {
      console.log(`  Table ${idx}: ${t.name}`);
      console.log(`    - relationCommonNotes (${t.relationCommonNotes?.length || 0}):`,
        t.relationCommonNotes?.map(n => `${n.codeId}`) || []);
      console.log(`    - relationTestNotes (${t.relationTestNotes?.length || 0}):`,
        t.relationTestNotes?.map(n => n.note?.substring(0, 30)) || []);
      console.log(`    - relationAdditionalNotes (${t.relationAdditionalNotes?.length || 0}):`,
        t.relationAdditionalNotes?.map(n => n.note?.substring(0, 30)) || []);
    });

    const dialogRef = this.dialog.open(AddPressureTempTableComponent, {
      width: '95vw',
      height: '90vh',
      maxWidth: '1400px',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '500px',
      disableClose: false,
      panelClass: ['ptt-modal-dialog', 'responsive-modal'],
      data: modalData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.handlePTTModalResult(result);
      }
    });
  }

  /**
   * Handles the result from the PTT modal
   */
  private async handlePTTModalResult(result: PressureTemperatureTableViewModel[]): Promise<void> {
    if (result && Array.isArray(result)) {

      this.pttSyncStates.clear();
      this.pttSyncLoading.clear();

      // Ensure all tables are proper PressureTemperatureTableViewModel instances AND sync to latest versions
      const properInstances = await Promise.all(result.map(async (table, index) => {
        let properInstance: PressureTemperatureTableViewModel;

        if (table instanceof PressureTemperatureTableViewModel) {
          properInstance = table;
        } else {
          properInstance = PressureTemperatureTableViewModel.fromJS(table);
        }

        try {
          const validationResult = await this.pttSyncService.validateAndGetLatestPTT(properInstance.id).toPromise();

          if (!validationResult.isCurrent && validationResult.latestVersion) {
            const latestInstance = PressureTemperatureTableViewModel.fromJS(validationResult.latestVersion);
            return latestInstance;
          } else {
            return properInstance;
          }
        } catch (error) {
          return properInstance;
        }
      }));

      const existingTables = this.pipeClassViewModel?.pressureTemperatureTables || [];

      // Create a map of new tables by name for fast lookup
      const newTablesByName = new Map();
      properInstances.forEach((table, index) => {
        newTablesByName.set(table.name, table);
      });

      // Filter out existing tables that will be replaced (have same name as new ones)
      const filteredExistingTables = existingTables.filter(existingTable => {
        const willBeReplaced = newTablesByName.has(existingTable.name);
        return !willBeReplaced;
      });

      // Combine filtered existing + all new tables
      const finalTables = [...filteredExistingTables, ...properInstances];
      const modalTablesByName = new Map<string, PressureTemperatureTableViewModel>();
      properInstances.forEach(table => {
        modalTablesByName.set(table.name, table);
      });
      this._pipeClassViewModel.pressureTemperatureTables.length = 0; // Clear array
      this._pipeClassViewModel.pressureTemperatureTables.push(...finalTables); // Add new tables

      this._pipeClassViewModel.pressureTemperatureTables.forEach((table, idx) => {
        const modalTable = modalTablesByName.get(table.name);

        if (modalTable && modalTable.relationCommonNotes?.length > 0) {
          table.relationCommonNotes = modalTable.relationCommonNotes || [];
          table.relationTestNotes = modalTable.relationTestNotes || [];
          table.relationAdditionalNotes = modalTable.relationAdditionalNotes || [];
        } else if (modalTable) {
          table.relationCommonNotes = [];
          table.relationTestNotes = [];
          table.relationAdditionalNotes = [];
        } else {
          table.relationCommonNotes = [];
          table.relationTestNotes = [];
          table.relationAdditionalNotes = [];
        }

        (table as any).pipeClassPressureTemperatureTableRelationNotes = [];
        (table as any).relationNotes = [];
        (table as any).pressureTemperatureTableRelationNotes = [];
      });

      // Mark all final tables as changed (both kept existing + new ones)
      finalTables.forEach((table, index) => {
        this.markPTTChanged(index);
      });

      this.pttCommonNotes = [];
      this.pttHydrotestNotes = [];
      this.pttAdditionalNotes = [];
      this.pttNotesChanges = [];
      this.pttNotesInitialized = false; // Reset initialization flag


      this.initializePTTNotes();
      this.validateAllPTTTables();
      this.cdr.markForCheck();
      this.cdr.detectChanges();

    }
  }

  // ========== NOTES EDITING METHODS ==========

  /**
   * Start editing Service Notes
   */
  startEditServiceNotes(): void {
    const currentNotes = this.getNotesForProperty('service');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Service Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('service', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-service-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Material Notes
   */
  startEditMaterialNotes(): void {
    const currentNotes = this.getNotesForProperty('material');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Material Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('material', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-material');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Rating Class Notes
   */
  startEditRatingClassNotes(): void {
    const currentNotes = this.getNotesForProperty('ratingClass');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Rating Class Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('ratingClass', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-rating-class');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Design Code Notes
   */
  startEditDesignCodeNotes(): void {
    const currentNotes = this.getNotesForProperty('designCode');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Design Code Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('designCode', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-design-code-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Temperature Limit Notes
   */
  startEditTemperatureLimitNotes(): void {
    const currentNotes = this.getNotesForProperty('temperatureRange');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Temperature Limit Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('temperatureRange', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-temperature-limit-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Stress Relief Notes
   */
  startEditStressReliefNotes(): void {
    const currentNotes = this.getNotesForProperty('stressRelief');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Stress Relief Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('stressRelief', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-stress-relief-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Corrosion Allowance Notes
   */
  startEditCorrosionAllowanceNotes(): void {
    const currentNotes = this.getNotesForProperty('nominalCorrosionAllowance');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Corrosion Allowance Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('nominalCorrosionAllowance', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-corrosion-allowance-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Examination Notes
   */
  startEditExaminationNotes(): void {
    const currentNotes = this.getNotesForProperty('examination');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Examination Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('examination', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-examination-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Valve Trim Notes
   */
  startEditValveTrimNotes(): void {
    const currentNotes = this.getNotesForProperty('valveTrim');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Valve Trim Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('valveTrim', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-valve-trim-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Start editing Limited By Notes
   */
  startEditLimitedByNotes(): void {
    const currentNotes = this.getNotesForProperty('limitedBy');

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: currentNotes,
        title: 'Select Limited By Notes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        const previousNotes = [...currentNotes];
        this.updateNotesForProperty('limitedBy', result);
        this.updateCommonNotesForField(previousNotes, result, 'summary-limited-by-notes');
        this.forceArrayReferenceUpdate();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Handles comments changes from the comments section component
   */
  onCommentsChanged(comments: CommentViewModel[]): void {
    if (comments && Array.isArray(comments)) {
      this.pipeClassComments = [...comments];
      // Emit to parent component for centralized management
      this.commentsChanged.emit(comments);
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle save comments request from child comments component
   */
  onSaveCommentsRequested(): void {
    // Forward the save request to parent component
    this.saveCommentsRequested.emit();
  }

  /**
   * Perform the actual save operation
   */
  private performSaveChanges(): void {
    // TODO: Implement the actual save logic
    // This could involve calling an API to save the pipeClassViewModel
    // You might want to emit an event to the parent component or call a service

    // For now, just show a success message or emit an event
    // this.snackBarService.showSnackBar(true, 'Changes saved successfully');

    // Example of what the save logic might look like:
    // this.esraPipingAPIClient.updatePipeClass(this.pipeClassViewModel).subscribe({
    //   next: () => {
    //     // Handle successful save
    //   },
    //   error: (error) => {
    //     // Handle save error
    //   }
    // });
  }

  /**
   * Perform the actual cancel/discard operation
   */
  private performCancelChanges(): void {
    // TODO: Implement the actual cancel logic
    // This could invoke resetting the form to its original state
    // or navigating back to a previous page

    // For now, you might want to:
    // 1. Reset all form changes
    // 2. Reload the original data
    // 3. Navigate back to the search results

    // Example of what the cancel logic might look like:
    // this.router.navigate(['/search']);
  }

  /**
   * Update common notes for a specific field by emitting add/remove events
   */
  private updateCommonNotesForField(previousNotes: NoteOption[], currentNotes: NoteOption[], origin: string): void {

    // Determine which notes were added and which were removed
    const addedNotes = currentNotes.filter(newNote =>
      !previousNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
    );

    const removedNotes = previousNotes.filter(oldNote =>
      !currentNotes.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
    );


    // Emit added notes event
    if (addedNotes.length > 0) {
      this.notesAddedEvent.emit({
        notes: addedNotes,
        origin: origin
      });
    }

    // Emit removed notes event
    if (removedNotes.length > 0) {
      this.notesRemovedEvent.emit({
        notes: removedNotes,
        origin: origin
      });
    }

    // Trigger change detection and force UI refresh
    this.uiRefreshVersion++;
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  /**
   * Gets current common notes from pipeClassViewModel
   */
  private getCurrentCommonNotes(): NoteOption[] {
    return (this.pipeClassViewModel.customNotes || []).map((note, index) => ({
      id: note.id || `note-${index}`,
      code: `N-${index + 1}`, // Generate code since PipeClassNoteViewModel doesn't have codeId
      note: note.note || '',
      category: 'Custom Notes',
      selected: true
    }));
  }

  /**
   * Updates common notes in pipeClassViewModel
   */
  private updateCommonNotes(notes: NoteOption[]): void {
    if (!this.pipeClassViewModel.customNotes) {
      this.pipeClassViewModel.customNotes = [];
    }

    this.pipeClassViewModel.customNotes = notes.map(note => new PipeClassNoteViewModel({
      id: note.id,
      note: note.note
    }));
  }

  /**
   * Generate a valid GUID for new notes
   */
  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Validate if string is a valid GUID
   */
  private isValidGuid(str: string): boolean {
    if (!str) return false;
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(str);
  }

  /**
   * Gets notes for a specific property from pipeClassViewModel
   */
  private getNotesForProperty(propertyName: string): NoteOption[] {

    const property = this.pipeClassViewModel[propertyName];
    if (!property || !property.commonNotes) {
      return [];
    }

    const notes = property.commonNotes.map((note, index) => ({
      id: note.noteId || note.id || `${propertyName}-${index}`,
      code: note.codeId || `${propertyName.toUpperCase()}-${index + 1}`,
      note: note.note || '',
      category: propertyName,
      selected: true
    }));

    return notes;
  }

  /**
   * Updates notes for a specific property in pipeClassViewModel
   */
  private updateNotesForProperty(propertyName: string, notes: NoteOption[]): void {

    if (!this.pipeClassViewModel[propertyName]) {
      return;
    }

    if (!this.pipeClassViewModel[propertyName].commonNotes) {
      this.pipeClassViewModel[propertyName].commonNotes = [];
    }

    // Create proper CommonNoteViewModel instances to avoid toJSON errors
    // CommonNoteViewModel is already imported at the top of the file
    this.pipeClassViewModel[propertyName].commonNotes = notes.map(note => new CommonNoteViewModel({
      noteId: note.id,
      codeId: note.code,
      note: note.note,
      isActive: true,
      isArchived: false,
      isReferenced: false,
      updated: new Date()
    }));

    // Mark summary table as changed when notes are updated
    this.markSummaryTableChanged();

    // Emit propertyChanged event for proper parent synchronization
    this.propertyChanged.emit({
      field: propertyName + 'Notes',
      value: this.pipeClassViewModel[propertyName],
      section: 'summary'
    });
  }

  /**
   * Force array reference updates to trigger Angular change detection
   */
  private forceArrayReferenceUpdate(): void {
    // Force change detection for pipeClassViewModel properties
    this.cdr.detectChanges();
  }

  /**
   * Public method to get PTT additional notes for a specific table (used by parent component for save)
   */
  public getPTTAdditionalNotesForSave(tableIndex: number): string[] {
    const unifiedNotes = this.getPTTAdditionalNotes(tableIndex);
    // Return only the editable notes (new or modified) as strings for the API
    return unifiedNotes
      .filter(note => note.isEditable)
      .map(note => note.text);
  }

}
